import { Check, Plus } from "lucide-react";
import { FormEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type DashboardSection,
  loadAssetsData,
  loadDashboardData,
  loadFinanceData,
  loadGroceryData,
  loadInventoryData,
  loadMealAssemblerData,
  loadRecipeData,
  subscribeToDateDerivedAggregates,
  type DateDerivedScope
} from "./app/loaders.js";
import { clearShoppingOfflineState, loadShoppingDataWithOfflineFallback, patchCachedShoppingTrip } from "./features/shopping/offline.js";
import { activeGroceryList, formValue, mealFilterQuery, occurrenceBatches } from "./app/helpers.js";
import type {
  Account,
  AuthStatus,
  Asset,
  AssetMovement,
  AssetValueReport,
  Attachment,
  AuditEvent,
  Budget,
  FinanceOverview,
  Category,
  EntityReferenceId,
  AuthIdentity,
  HouseholdMembership,
  InsuranceCoverageReport,
  Location,
  MealAssemblerResponse,
  MealFilters,
  MealPlanEntry,
  Product,
  Recipe,
  RecurringItem,
  ScanData,
  ShoppingList,
  ShoppingTrip,
  StockLot,
  Store,
  Summary,
  TaskOccurrence,
  Transaction,
  User
} from "./app/types.js";
import { dispatchFeatureRefresh } from "./app/refreshEvents.js";
import { dispatchSelfFetchingRefresh } from "./app/tabRefresh.js";
import { planRealtimeError, remainingReconnectGraceMs } from "./app/realtimeConnection.js";
import {
  beginSelfRefreshCycle,
  createSelfRefreshMarks,
  isOwnRefreshedEcho as isOwnRefreshedEchoFor,
  noteWriteIssued,
  requeueSelfRefreshCorrection,
  takeSelfRefreshCorrections
} from "./app/selfRefresh.js";
import { api, ApiError, displayErrorMessage, friendlyNetworkMessage, isDemoModeRejection, isTransientNetworkError, postJson } from "./lib/api.js";
import { subscribeSessionExpired } from "./lib/sessionEvents.js";
import { UnreachableScreen } from "./features/shell/UnreachableScreen.js";
import { attachmentUploadBody, isResolvedEntityId, primaryAttachmentIdForQueuedUpload, reorderAssetPhotos, uploadedPhotoAttachmentIds } from "./lib/attachments.js";
import { defaultAttachmentCollectionType, type AttachmentCollectionType } from "@household/shared/attachments";
import { backgroundUploadFailureMessage, backgroundUploads } from "./lib/backgroundUploads.js";
import { purgeOfflineApiCache, signOutWithTeardown } from "./lib/offlineCache.js";
import { flushQueue, subscribePendingCount } from "./lib/offlineQueue.js";
import { applyPublicPageTheme, applyThemePreference, normalizeThemePreference, readStoredThemePreference, rememberSessionHint } from "./lib/theme.js";
import { copy } from "./app/i18n.js";
import { useI18nStartup } from "./app/useI18nStartup.js";
import { useAuthStartup } from "./app/useAuthStartup.js";
import { useStartupStages } from "./app/useStartupStages.js";
import { StartupScreen } from "./features/shell/StartupScreen.js";
import { I18nTransitionBoundary } from "./features/shell/I18nTransitionBoundary.js";
import { RouteNotFoundView } from "./features/shell/RouteNotFoundView.js";
import { getResolvedLocale, resolveLocale, setResolvedLocale, type ResolvedLocale } from "./app/locale.js";
import { onWriteIssued } from "./lib/writeIdentity.js";
import { setExpectedIdentity } from "./lib/expectedIdentity.js";
import { HouseholdProvider, type LocalWriteCycle, type RefreshScope, type RunOptions, type ToastAction, type ToastMessage, type ToastOptions } from "./features/context.js";
import { ListSheetDialogProvider } from "./features/common/listSheet/ListSheetDialog.js";
import { readStoredAssistantChatId } from "./features/assistant/chatStorage.js";
import { readAssistantSeed } from "./features/assistant/seed.js";
import { nextSnoozeDate } from "./features/chores/formPayload.js";
import { choresListLink, useChoreMutations, useChoresState } from "./features/chores/useChoresData.js";
import { isPublicPagePath, marketingRoute, signInPath, type MarketingRoute } from "./features/marketing/routing.js";
import { Shell, tabFromPath, tabPath, type Tab } from "./features/shell/navigation.js";
// Every tab's view is a lazy chunk (#1678); the declarations sit beside the
// tab registry so this file does not grow by one line per view.
import { MarketingSiteLazy, ViewSuspense, views } from "./features/shell/views.js";
import { CommandPalette } from "./components/CommandPalette.js";
import { assetDetailsPath, assetIdFromPath, assistantChatPath, currentRoute, foodDetailsPath, navigateBrowser, placeDetailsPath, placeIdFromPath, productIdFromPath, recipeDetailsPath, recipeIdFromPath } from "./app/routing.js";

// Fixed defaults for the kitchen signals (ready-now / use-soon) the Meals hub's
// "dinner tonight" page reads from mealData. The interactive Meal Assembler
// filter UI was retired; deeper "what can I make" exploration now goes through
// the AI assistant's meal tools (#589/#615).
const defaultMealFilters: MealFilters = {
  maxMissingItems: 3,
  maxCookMinutes: "",
  includeStaples: true,
  prioritizeExpiring: true,
  simpleMealsOnly: false,
  recipesOnly: false,
  includeCatalog: true,
  includeLlm: false,
  includePending: false,
  tags: ""
};
const defaultMealQuery = mealFilterQuery(defaultMealFilters);

const realtimeCursorStoragePrefix = "household.realtime.cursor";

// `realtimeReconnectGraceMs` and the reopen/backoff rules now live in
// app/realtimeConnection.ts, where they are tested without a DOM (#574).

type RealtimeConnectionState = "connected" | "reconnecting";

type HouseholdRealtimeEvent = {
  id?: number;
  scopes?: string[];
  authChanged?: boolean;
  refreshAuth?: boolean;
  cursor?: number;
  /** Set by the server when this session's user is the human who made the change. */
  self?: boolean;
  /**
   * The client-chosen id of the write this event announces (#800). Absent when
   * no browser made it — an agent tool run, a script — or when the write came
   * from a client too old to send one.
   */
  writeId?: string;
};

function realtimeCursorStorageKey(userId: string, householdId: string): string {
  return `${realtimeCursorStoragePrefix}.${userId}.${householdId}`;
}

function readRealtimeCursor(userId: string, householdId: string): number {
  try {
    const value = window.localStorage.getItem(realtimeCursorStorageKey(userId, householdId));
    const cursor = Number(value);
    return Number.isSafeInteger(cursor) && cursor > 0 ? cursor : 0;
  } catch {
    return 0;
  }
}

function rememberRealtimeCursor(userId: string, householdId: string, cursor: number | undefined): void {
  if (typeof cursor !== "number" || !Number.isSafeInteger(cursor) || cursor < 1) return;
  try {
    window.localStorage.setItem(realtimeCursorStorageKey(userId, householdId), String(cursor));
  } catch {
    // Local storage may be unavailable in installed or privacy-restricted PWA contexts.
  }
}

function eventAffectsTab(event: HouseholdRealtimeEvent, tab: Tab): boolean {
  return Boolean(event.authChanged || event.refreshAuth || event.scopes?.includes(tab));
}

function occurrenceIds(occurrences: Array<{ id: EntityReferenceId }>): EntityReferenceId[] {
  const seen = new Set<string>();
  return occurrences.flatMap((occurrence) => {
    const key = String(occurrence.id);
    if (seen.has(key)) return [];
    seen.add(key);
    return [occurrence.id];
  });
}

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return count === 1 ? singular : plural;
}

type GoogleAuthPopupMessage = {
  source?: string;
  ok?: boolean;
  message?: string;
  redirect?: string;
};

function googleAuthPopupFeatures() {
  const width = 520;
  const height = 680;
  const left = Math.max(0, window.screenX + Math.round((window.outerWidth - width) / 2));
  const top = Math.max(0, window.screenY + Math.round((window.outerHeight - height) / 2));
  return `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
}

/**
 * Why a Google sign-in attempt ended, as a STABLE code (#1088).
 *
 * This helper used to build and throw finished English — "Popup blocked…",
 * "Google sign-in failed." — which `run()` then put into the auth alert, so the
 * one screen a Spanish household sees BEFORE anything else went back to English
 * the moment it went wrong. The code is what the app reasons about; the
 * sentence is looked up at the throw site.
 *
 * `copy` rather than a `t` from a hook: this is a module-level helper called
 * from `signInWithGoogle`, and the sign-in screen renders before any feature
 * namespace is fetched. `common` is one of the two eagerly initialised
 * namespaces (app/i18n.ts), so it is the only bundle guaranteed to be loaded
 * this early — and App does not mount AuthScreens until `i18nReady`.
 */
type GoogleAuthFailureCode = "popup_blocked" | "sign_in_failed" | "window_closed";

class GoogleAuthError extends Error {
  constructor(readonly code: GoogleAuthFailureCode, message: string, readonly detail?: string) {
    super(message);
    this.name = "GoogleAuthError";
  }
}

function googleAuthFailure(code: GoogleAuthFailureCode, detail?: string): GoogleAuthError {
  const sentence = code === "popup_blocked"
    ? copy("auth.google.popupBlocked", "Popup blocked. Allow popups for this app, then sign in with Google again.")
    : code === "window_closed"
      ? copy("auth.google.closed", "Google sign-in window was closed before authentication finished.")
      : copy("auth.google.failed", "Google sign-in failed.");
  // The callback page can word the failure itself, but that text is the
  // server's — English, and sometimes a raw OAuth token like "access_denied".
  // It is kept as a parenthetical diagnostic rather than as the sentence, the
  // same way an upload reason keeps "(HTTP 500)": the reader always gets a
  // sentence in their language and an owner still sees what Google said.
  const trimmed = detail?.trim();
  const message = trimmed
    ? copy("auth.google.failedDetail", `${sentence} (${trimmed})`, { message: sentence, detail: trimmed })
    : sentence;
  return new GoogleAuthError(code, message, trimmed);
}

async function openGoogleAuthPopup(): Promise<string> {
  const popup = window.open("about:blank", "household-google-sign-in", googleAuthPopupFeatures());
  if (!popup) throw googleAuthFailure("popup_blocked");
  try {
    // This document IS reachable: it is same-origin `about:blank` written from
    // here, so it reads the parent's already-loaded bundle. Only the page it
    // navigates to next belongs to the API and to Google, and that one is
    // beyond any bundle this app ships.
    popup.document.documentElement.lang = document.documentElement.lang;
    popup.document.title = copy("auth.google.popupTitle", "Sign in with Google");
    const loading = popup.document.createElement("p");
    loading.setAttribute("style", "font: 15px system-ui; padding: 18px;");
    // textContent, not innerHTML: this is copy from a bundle, and copy is data.
    loading.textContent = copy("auth.google.popupLoading", "Opening Google sign-in...");
    popup.document.body.replaceChildren(loading);
  } catch {
    // The popup may become cross-origin before the loading text is written.
  }

  let authUrl = "";
  let callbackOrigin = window.location.origin;
  try {
    const response = await api<{ authUrl: string; callbackOrigin?: string }>("/api/v1/auth/google/start");
    authUrl = response.authUrl;
    callbackOrigin = response.callbackOrigin || callbackOrigin;
  } catch (error) {
    popup.close();
    throw error;
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const allowedOrigins = new Set([window.location.origin, callbackOrigin]);
    const settle = (work: () => void) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      window.clearInterval(closedTimer);
      work();
    };
    const onMessage = (event: MessageEvent<GoogleAuthPopupMessage>) => {
      if (!allowedOrigins.has(event.origin)) return;
      const data = event.data;
      if (data?.source !== "household-google-auth") return;
      settle(() => {
        // The success string is never rendered — signInWithGoogle discards it —
        // so it stays the server's own text rather than earning a key.
        if (data.ok) resolve(data.message || "Signed in with Google.");
        else reject(googleAuthFailure("sign_in_failed", data.message));
      });
    };
    const closedTimer = window.setInterval(() => {
      if (popup.closed) {
        settle(() => reject(googleAuthFailure("window_closed")));
      }
    }, 500);
    window.addEventListener("message", onMessage);
    popup.location.href = authUrl;
    popup.focus();
  });
}

export function App() {
  const initialRoute = useMemo(() => currentRoute(), []);
  const [tab, setActiveTab] = useState<Tab>(initialRoute.tab);
  // #1259: the URL named no page of this app. Held beside `tab` rather than
  // encoded into it, because the shell still needs a real tab to frame the
  // not-found view.
  //
  // Cleared beside EVERY setActiveTab that names a real tab, plus in
  // handleSignedOut. Landing on a page is what leaves the not-found state, and
  // the URL moves through pushState/replaceState, which fires no popstate — so
  // adoptRouteFromLocation (the one place that SETS it from the URL, on
  // Back/Forward) cannot be relied on to clear it. Reasoning about which
  // callers were "reachable from a 404" got this wrong once already: /signin is
  // not a tab, so a fresh sign-in mounted with the flag set and rendered the
  // 404 over the Dashboard.
  const [routeNotFound, setRouteNotFound] = useState(initialRoute.notFound);
  // AssistantView owns its own data loads, so tab refreshes reach it through a
  // nonce prop instead of an App-level loader (#278).
  const [assistantRefreshNonce, setAssistantRefreshNonce] = useState(0);
  const [assetRouteId, setAssetRouteId] = useState<EntityReferenceId | null>(initialRoute.assetId);
  // #980: the open place page. Mutually exclusive with assetRouteId — both set
  // renders two surfaces at once and makes the URL disagree with the screen.
  const [placeRouteId, setPlaceRouteId] = useState<EntityReferenceId | null>(initialRoute.placeId);
  // The open food page (#1045). Cloned from the asset/place lifecycle rather
  // than invented: init from the route, follow popstate, clear on tab nav and
  // on sign-out. Inventing a second lifecycle is how these two drift.
  const [productRouteId, setProductRouteId] = useState<EntityReferenceId | null>(initialRoute.productId);

  const openFoodDetails = useCallback((productId: EntityReferenceId, options: { replace?: boolean } = {}) => {
    // setActiveTab is part of opening, exactly as it is for places, things and
    // recipes: the food page lives on the groceries tab, so a caller anywhere
    // else (the scan landing, an audit toast) would otherwise change the URL
    // and leave the old tab on screen — browser-verified, the scan landing did
    // precisely that.
    setActiveTab("groceries");
    setRouteNotFound(false);
    setProductRouteId(productId);
    navigateBrowser(foodDetailsPath(productId), options.replace ?? false);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const closeFoodDetails = useCallback((options: { replace?: boolean } = {}) => {
    setProductRouteId(null);
    if (productIdFromPath(window.location.pathname)) navigateBrowser(tabPath("groceries"), options.replace ?? true);
  }, []);
  const [recipeRouteId, setRecipeRouteId] = useState<EntityReferenceId | null>(initialRoute.recipeId);
  // The chat uuid from an /assistant/{uuid} URL (#603); null on bare /assistant
  // (the new-chat page). AssistantView adopts changes and reports its own
  // session switches back through handleAssistantChatRoute.
  const [assistantChatRouteId, setAssistantChatRouteId] = useState<string | null>(initialRoute.assistantChatId);
  const [releaseNotesVersion, setReleaseNotesVersion] = useState<string | null>(initialRoute.releaseNotesVersion);
  // The command palette is mounted as a SIBLING of Shell (below), so the header
  // inside Shell cannot reach it through context; App owns the flag and hands
  // each side its half (#1055).
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [identity, setIdentity] = useState<AuthIdentity | null>(null);
  const [user, setUser] = useState<User | null>(null);
  // The URL the PUBLIC pages read (#833). The signed-in app routes by tab, but
  // "/" means the landing page to a signed-out visitor and the Dashboard to
  // everyone else, so the raw pathname has to be state of its own. It is kept
  // here rather than inside MarketingSite so an in-page link, the browser Back
  // button and a sign-in all move the same value.
  const [publicPath, setPublicPath] = useState(() => window.location.pathname);
  // Computed here rather than at the render site below because the theme effect
  // needs it, and a hook cannot sit after the early returns.
  //
  // `identity` counts as a session, not just `user`. Someone who has signed in
  // with Google but does not belong to a household yet has an identity and no
  // user, and the create-or-join-household panel is rendered further down this
  // function — so treating them as a signed-out visitor would hide the only
  // screen that can finish their sign-up behind the landing page.
  const hasSession = Boolean(user) || Boolean(identity);
  const matchedPublicRoute = marketingRoute(publicPath, hasSession);
  // A URL that is neither a public page nor a page of the app, shown to a
  // visitor with no session. Only App knows both vocabularies, which is why the
  // route is built here rather than by marketingRoute.
  //
  // tabFromPath is the guard that matters: a signed-out /chores or
  // /inventory/assets/abc is a REAL app deep link, and it has to keep falling
  // through to the sign-in form so the visitor lands where they were going once
  // they are through it. Only a path the app cannot name reaches the 404. A
  // visitor WITH a session is left alone as well — the app shell answers for its
  // own URLs, and this is not the place to change that.
  const unknownPublicPath = !matchedPublicRoute
    && !hasSession
    && (publicPath.replace(/\/+$/, "") || "/") !== signInPath
    && tabFromPath(publicPath) === null;
  const publicRoute: MarketingRoute | null = matchedPublicRoute ?? (unknownPublicPath ? { kind: "notFound" } : null);
  // Theming asks a wider question than rendering: /signin is a light public
  // surface too, though it is not a marketing route (see isPublicPagePath).
  const onPublicPage = isPublicPagePath(publicPath, hasSession) || unknownPublicPath;
  // currentRoute only knows the app's own vocabulary, so it flags every public
  // URL — /signin, /contact, /product/* — as not-found. They are real pages,
  // just not tabs, and this is the one place that knows both vocabularies.
  //
  // /signin is the case that bites: a visitor signs in there, the effect below
  // moves them to the Dashboard, and without this the shell rendered "This page
  // does not exist" over "/" for a session that had just started (#1259).
  const showRouteNotFound = routeNotFound && !onPublicPage;
  // Routing and the buttons ask different questions. Routing asks "does this
  // visitor have an account?", so that "/" stops being the landing page as soon
  // as one exists. The public pages' calls to action ask "can this visitor open
  // a module?", which needs an actual household — offer "Open the app" to
  // someone who has none and the app can only bounce them back to create-or-join.
  const hasHousehold = Boolean(user);
  const activeUserId = user?.id ?? "";
  const activeHouseholdId = user?.householdId ?? "";
  const [households, setHouseholds] = useState<HouseholdMembership[]>([]);
  const [authReady, setAuthReady] = useState(false);
  // The first auth-status read failed at the network layer (September audit):
  // not "signed out", and not something to ask for a password over. Bumping
  // `authProbe` re-runs the read; the online event does it on its own.
  const [authUnreachable, setAuthUnreachable] = useState(false);
  const [authProbe, setAuthProbe] = useState(0);
  // Translation consumers must not render before the runtime can translate:
  // `t()` before init returns the bare last segment of the key, so the shell
  // would paint "now" and "brand" and then correct itself (#1088).
  const [setupRequired, setSetupRequired] = useState(false);
  const [authMode, setAuthMode] = useState<AuthStatus["authMode"]>("local_password");
  const [googleAuthConfigured, setGoogleAuthConfigured] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [error, setErrorText] = useState("");
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const toastIdRef = useRef(0);
  const [busy, setBusy] = useState(false);
  const [realtimeState, setRealtimeState] = useState<RealtimeConnectionState>("connected");
  const [pendingRealtimeUpdates, setPendingRealtimeUpdates] = useState(0);
  // Offline-first (#500): navigator.onLine drives the shell's offline indicator,
  // and pendingSyncCount tracks writes queued in the IndexedDB outbox awaiting
  // replay. The realtime "reconnecting" banner is suppressed while offline so the
  // two indicators don't contradict each other.
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine !== false));
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  // Attachment files still uploading in the background (#522).
  const [pendingUploadCount, setPendingUploadCount] = useState(0);
  const [scanToken, setScanToken] = useState(initialRoute.scanToken);
  const [summary, setSummary] = useState<Summary | null>(null);
  // The dashboard reads that did not come back on the last load (September
  // audit). Each panel reads it to show "couldn't load" instead of its empty
  // copy; the data for those sections keeps its previous value.
  const [dashboardLoadFailures, setDashboardLoadFailures] = useState<DashboardSection[]>([]);
  const [assetValue, setAssetValue] = useState<AssetValueReport | null>(null);
  const [insuranceCoverage, setInsuranceCoverage] = useState<InsuranceCoverageReport | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetQuery, setAssetQuery] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [stockLots, setStockLots] = useState<StockLot[]>([]);
  const [expirations, setExpirations] = useState<StockLot[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [mealPlanEntries, setMealPlanEntries] = useState<MealPlanEntry[]>([]);
  const [shoppingLists, setShoppingLists] = useState<ShoppingList[]>([]);
  const [shoppingTrips, setShoppingTrips] = useState<ShoppingTrip[]>([]);
  // How many COMPLETE shopping payloads have landed for this household, and
  // whether one is out right now (#1311). An empty `shoppingLists` otherwise
  // reads the same whether the fetch is still in flight or the household really
  // has no lists, and a /shopping?listId=… deep link has to tell those apart.
  //
  // Counted rather than a "loaded" boolean, and bumped ONLY by `loadShopping`,
  // for two reasons a boolean got wrong (codex review):
  //   1. The dashboard and recipes payloads carry `shoppingLists` but no trips
  //      or stores, so a boolean they set made a ?tripId= deep link render
  //      "trip unavailable" for a trip that had simply not been fetched yet.
  //   2. "Loaded once" is not "loaded since you asked". A deep link to a list
  //      another member just created arrives with the tab's refresh still in
  //      flight; resolving it against the previous payload rejects a live id.
  //      The version and the in-flight flag together let the route wait for the
  //      refresh that is already on its way.
  const [shoppingPayloadVersion, setShoppingPayloadVersion] = useState(0);
  const [shoppingRefreshing, setShoppingRefreshing] = useState(false);
  // How many shopping fetches have FAILED with something a retry of the same
  // kind will not fix. A route waiting for a payload needs to hear about those:
  // the fetch it is waiting for is often the shell's own tab refresh, not one it
  // asked for, and a failure it never sees is a wait that never ends (codex
  // review). Counted rather than a flag so a waiting route can tell a failure
  // that happened after it asked from one that happened before.
  const [shoppingPayloadFailures, setShoppingPayloadFailures] = useState(0);
  const shoppingFetchesInFlight = useRef(0);
  // Latest-response-wins across overlapping refreshes (codex review). Counting
  // in-flight requests is not enough: a tab entry and a realtime echo can be out
  // together, and if the OLDER one answers last it would overwrite the newer
  // payload with data that predates it — then bump the version and report
  // settled, so a list created in between reads as deleted.
  const shoppingRequestSeq = useRef(0);
  const shoppingAppliedSeq = useRef(0);
  // Which household's requests these counters are about. Bumped when household
  // state is cleared, so a request from the previous one neither applies its
  // payload nor touches the new household's in-flight bookkeeping.
  const shoppingLoadGeneration = useRef(0);
  const [stores, setStores] = useState<Store[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [recurringItems, setRecurringItems] = useState<RecurringItem[]>([]);
  const [financeOverview, setFinanceOverview] = useState<FinanceOverview | null>(null);
  const [audit, setAudit] = useState<AuditEvent[]>([]);
  const [taskOccurrences, setTaskOccurrences] = useState<TaskOccurrence[]>([]);
  // Chores own their state (#1682): the eight fields, the loader and the reset
  // live in features/chores/useChoresData.ts; the mutations join further down,
  // once `run` and the toast exist.
  const choresState = useChoresState({ user, setLocations });
  const { choreView, setChoreView, choreOccurrences, choreCategories, choreTemplates, choreAnalytics, choreUsers, choreTimeline, selectedChore, setSelectedChore, loadChores, resetChores } = choresState;
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  // Read by detached background work that must not touch state to find out what
  // is on screen — a state updater has to stay pure (#733 review).
  const selectedAssetRef = useRef<Asset | null>(null);
  selectedAssetRef.current = selectedAsset;
  const [assetNotFound, setAssetNotFound] = useState(false);
  const assetNotFoundIdRef = useRef<EntityReferenceId | null>(null);
  // Non-404 deep-link load failure (network error, 500, offline) — surfaced as a
  // retryable error state in the detail panel instead of an endless spinner (#385).
  const [assetLoadError, setAssetLoadError] = useState<string | null>(null);
  const [assetLoadAttempt, setAssetLoadAttempt] = useState(0);
  const [assetMovements, setAssetMovements] = useState<AssetMovement[]>([]);
  const [assetAttachments, setAssetAttachments] = useState<Attachment[]>([]);
  const [scanData, setScanData] = useState<ScanData | null>(null);
  // Why the scanned code did not resolve: "not-found" for a code nothing is
  // labelled with, otherwise the failure's message (September audit). The
  // scan page shows it beside its input rather than the shell's red band.
  const [scanLoadError, setScanLoadError] = useState<string | null>(null);
  const [mealData, setMealData] = useState<MealAssemblerResponse | null>(null);
  const busyRef = useRef(busy);
  const tabRef = useRef(tab);
  const pendingRealtimeUpdatesRef = useRef(0);
  // Dirty bit for changes that refresh WITHOUT raising the banner (the user's
  // own edits, #932). It has to exist separately from the counter above, because
  // both resume paths key off that counter: a hidden or busy tab that only ever
  // saw silent events would otherwise advance its realtime cursor, never
  // increment anything, and stay stale forever once it came back.
  const pendingSilentRefreshRef = useRef(false);
  const pendingAuthRefreshRef = useRef(false);
  const realtimeRefreshTimerRef = useRef<number | null>(null);
  // Grace window before a dropped realtime connection surfaces the
  // "Reconnecting" banner: EventSource fires onerror on every transient blip and
  // auto-reconnects (usually sub-second), so a naive flip flashes the banner on
  // every screen constantly (#458). Only show it if the outage persists.
  const realtimeReconnectTimerRef = useRef<number | null>(null);
  // When the current outage began, or null while the stream is up. The grace
  // window is measured from here rather than from the last error, because the
  // app now tears the EventSource down and rebuilds it on a fatal close (#574)
  // and a fresh 5s timer per attempt would defer the banner indefinitely.
  const realtimeOutageSinceRef = useRef<number | null>(null);
  // Fatal closes (readyState CLOSED) since the last successful open, driving the
  // reopen backoff. Lives outside the effect so a reopen — which re-runs the
  // effect — does not reset the backoff it is part of.
  const realtimeReopenAttemptsRef = useRef(0);
  const realtimeReopenTimerRef = useRef<number | null>(null);
  // Bumped to force the realtime effect to build a fresh EventSource. Only the
  // fatal-close path uses it: a browser-driven retry must not get a second,
  // duplicate subscription.
  const [realtimeEpoch, setRealtimeEpoch] = useState(0);
  // When the user is actively editing a surface that manages its own optimistic
  // state (the shopping list sheet), the echo of their own change should not
  // raise the "updates available" banner and shove the page around. The surface
  // opens a short quiet window; echoes inside it refresh context silently.
  const quietRealtimeUntilRef = useRef(0);
  // Scopes this user's own writes have refreshed recently, and how long the SSE
  // echo of each of those changes stays redundant (#520). Keyed by scope so two
  // concurrent writes on different scopes cannot clobber each other's mark.
  //
  // When a cycle finishes without being able to vouch for an echo it already
  // swallowed, it queues a corrective refresh here rather than waiting for a
  // further event: the realtime cursor advanced past the dropped one, so there
  // is nothing left to wait for. The queue is drained on a 0ms timer so a burst
  // of overlapping cycles discovering the same thing costs one pass.
  const applySelfRefreshCorrectionsRef = useRef<() => void>(() => undefined);
  const selfRefreshCorrectionTimerRef = useRef<number | null>(null);
  const scheduleSelfRefreshCorrection = useCallback(() => {
    if (selfRefreshCorrectionTimerRef.current !== null) return;
    selfRefreshCorrectionTimerRef.current = window.setTimeout(() => {
      selfRefreshCorrectionTimerRef.current = null;
      applySelfRefreshCorrectionsRef.current();
    }, 0);
  }, []);
  const selfRefreshEchoRef = useRef(createSelfRefreshMarks(scheduleSelfRefreshCorrection));

  // Every write this session issues is recorded against the suppression ledger
  // as it goes out (#800), so the echo that comes back can be told apart from
  // another tab's and from one no refresh covers. The api client publishes
  // through lib/writeIdentity because `lib` cannot reach into a mounted App.
  useEffect(() => onWriteIssued((writeId) => {
    noteWriteIssued(selfRefreshEchoRef.current, writeId);
  }), []);
  const applyRealtimeUpdatesRef = useRef<() => Promise<void>>(async () => undefined);

  const canUseFinance = Boolean(user && (user.role === "owner" || user.financeAccess));
  // The #465 grocery-vs-project rule lives in `activeGroceryList` (helpers), so
  // the Things panel's own fallback fetch (#1198) cannot come to a different
  // answer than this one.
  const groceryLists = shoppingLists.filter((list) => list.kind !== "project");
  const activeList = activeGroceryList(shoppingLists);

  const setError = useCallback((message: string) => {
    const trimmed = message.trim();
    // Offline, "Can't reach the server" duplicates the offline banner above
    // it and contradicts nothing it says; the banner is the one message
    // (September audit). Every other failure still shows.
    if (trimmed && trimmed === friendlyNetworkMessage() && typeof navigator !== "undefined" && navigator.onLine === false) return;
    setErrorText(trimmed ? displayErrorMessage(trimmed, trimmed) : "");
  }, []);

  // Everything that belongs to the household we are leaving. React state is only
  // part of it: the shopping cache and the service worker's API response cache
  // both outlive a re-render, and the API cache is keyed by URL with no
  // household dimension, so a survivor there is served to whoever comes next
  // (#1003). Returning the purge lets a caller that is about to load the *new*
  // household's data await it; every caller gets the purge either way, which is
  // the point of doing it here rather than at each call site.
  const clearHouseholdData = useCallback((): Promise<void> => {
    clearShoppingOfflineState();
    setSummary(null);
    setDashboardLoadFailures([]);
    setAssetValue(null);
    setLocations([]);
    setAssets([]);
    setProducts([]);
    setStockLots([]);
    setExpirations([]);
    setRecipes([]);
    setMealPlanEntries([]);
    setShoppingLists([]);
    setShoppingTrips([]);
    setShoppingPayloadVersion(0);
    setShoppingPayloadFailures(0);
    setShoppingRefreshing(false);
    shoppingFetchesInFlight.current = 0;
    // A request already out belongs to the household being left: bumping the
    // generation makes it apply nothing and, just as importantly, decrement
    // nothing. Zeroing the counter alone was not enough — the old request's
    // `finally` still ran, taking the NEW household's in-flight count with it and
    // reporting its refresh settled while the payload was still on the way, which
    // is exactly the state a deep link must not be resolved against (codex
    // review).
    shoppingLoadGeneration.current += 1;
    setAccounts([]);
    setCategories([]);
    setTransactions([]);
    setBudgets([]);
    setRecurringItems([]);
    setFinanceOverview(null);
    setAudit([]);
    setTaskOccurrences([]);
    resetChores();
    setSelectedAsset(null);
    setAssetMovements([]);
    setAssetAttachments([]);
    setScanData(null);
    setScanLoadError(null);
    setMealData(null);
    return purgeOfflineApiCache();
  }, [resetChores]);

  const applyAuthStatus = useCallback((status: AuthStatus) => {
    setSetupRequired(status.setupRequired);
    setAuthMode(status.authMode ?? "local_password");
    setGoogleAuthConfigured(Boolean(status.googleAuthConfigured));
    setDemoMode(Boolean(status.demoMode));
    setIdentity(status.identity);
    setHouseholds(status.households ?? status.user?.households ?? []);
    setUser(status.user);
    // What every later request from this tab says it expects (#105). The
    // server only logs a mismatch; see lib/expectedIdentity.ts.
    setExpectedIdentity({ householdId: status.user?.householdId ?? null, userId: status.identity?.id ?? status.user?.id ?? null });
    // The theme is NOT applied here. It used to be, and that raced the public
    // pages: a signed-in visitor opening /product got the light surface on
    // mount and then this call repainted it with their own preference as soon
    // as auth resolved. One owner instead — the effect below, which sees both
    // the identity and the route (#843).
  }, []);

  const loadDashboard = useCallback(async () => {
    if (!user) return;
    const next = await loadDashboardData();
    // A section that failed is absent and keeps its previous value: stale
    // data with a "couldn't load" beside it beats a confident zero.
    if (next.summary !== undefined) setSummary(next.summary);
    if (next.assetValue !== undefined) setAssetValue(next.assetValue);
    if (next.insuranceCoverage !== undefined) setInsuranceCoverage(next.insuranceCoverage);
    if (next.expirations !== undefined) setExpirations(next.expirations);
    if (next.audit !== undefined) setAudit(next.audit);
    if (next.taskOccurrences !== undefined) setTaskOccurrences(next.taskOccurrences);
    if (next.shoppingLists !== undefined) setShoppingLists(next.shoppingLists);
    setDashboardLoadFailures(next.failed);
  }, [user]);

  const loadAssets = useCallback(async () => {
    if (!user) return;
    setAssets(await loadAssetsData());
  }, [user]);

  const loadInventory = useCallback(async () => {
    if (!user) return;
    const next = await loadInventoryData();
    setLocations(next.locations);
    setAssets(next.assets);
    setAssetValue(next.assetValue);
  }, [user]);

  const loadGroceries = useCallback(async () => {
    if (!user) return;
    const next = await loadGroceryData();
    setLocations(next.locations);
    setProducts(next.products);
    setStockLots(next.stockLots);
    setExpirations(next.expirations);
  }, [user]);

  // `catalog: false` on the Recipes tab (#1677): that view fetches its own
  // card-sized pages, so the shell only refreshes the plan and the lists there.
  const loadRecipes = useCallback(async (options: { catalog?: boolean } = {}) => {
    if (!user) return;
    const next = await loadRecipeData(options);
    if (next.recipes) setRecipes(next.recipes);
    setMealPlanEntries(next.mealPlanEntries);
    setShoppingLists(next.shoppingLists);
  }, [user]);

  const loadMealAssembler = useCallback(async (signal?: AbortSignal) => {
    // Fixed default query — the Meals hub reads mealData for the "ready now" /
    // "use soon" dinner signals; the assistant handles deeper filtering.
    if (!user) return;
    setMealData(await loadMealAssemblerData(defaultMealQuery, signal));
  }, [user]);

  const loadShopping = useCallback(async () => {
    if (!user) return;
    const generation = shoppingLoadGeneration.current;
    shoppingFetchesInFlight.current += 1;
    shoppingRequestSeq.current += 1;
    const seq = shoppingRequestSeq.current;
    setShoppingRefreshing(true);
    try {
      const next = await loadShoppingDataWithOfflineFallback();
      // Dropped whole — payload and version together, so nothing downstream
      // mistakes it for fresh state — when this response belongs to a household
      // that has since been left, or is older than one already applied.
      if (generation !== shoppingLoadGeneration.current || seq < shoppingAppliedSeq.current) return;
      shoppingAppliedSeq.current = seq;
      setShoppingLists(next.shoppingLists);
      setShoppingTrips(next.shoppingTrips);
      setStores(next.stores);
      // A cache served because the network could not be reached is the right
      // thing to render, but it is not an answer about what exists now, so it
      // does not advance the version (codex review). Otherwise a deep link to a
      // list created since that cache was written would be declared dead against
      // it while offline, and reconnecting could not bring it back.
      if (!next.servedFromCache) setShoppingPayloadVersion((version) => version + 1);
    } catch (error) {
      // A transient network failure is not counted: the offline fallback already
      // answers from cache where it can, and where it cannot, reconnecting is
      // the recovery path a waiting route should hold out for. Anything else —
      // a 5xx, an expired session — has no such path, so it is reported and the
      // waiting ends.
      if (generation === shoppingLoadGeneration.current && !isTransientNetworkError(error)) {
        setShoppingPayloadFailures((failures) => failures + 1);
      }
      throw error;
    } finally {
      // Counted, not a bare boolean: two overlapping refreshes (a tab entry and
      // a realtime echo) would otherwise have the first one to finish report
      // "settled" while the second is still out. A request from a household that
      // has since been left counts for nothing here — its decrement would be
      // taken out of the new household's tally.
      if (generation === shoppingLoadGeneration.current) {
        shoppingFetchesInFlight.current = Math.max(0, shoppingFetchesInFlight.current - 1);
        if (shoppingFetchesInFlight.current === 0) setShoppingRefreshing(false);
      }
    }
  }, [user]);

  // In-store check-off patches only the trip it touched (#702) instead of
  // refetching every list, trip and store on each tap. The updater form keeps
  // concurrent taps composing: each one reads the state the previous one left,
  // so two overlapping check-offs can't clobber each other. The same update also
  // goes to the offline cache, which the old `await loadShopping()` used to
  // rewrite on every tap — dropping that write would have left a reload on a
  // flaky in-store link showing a cart the shopper had already filled as empty.
  const patchShoppingTrip = useCallback((tripId: string, update: (trip: ShoppingTrip) => ShoppingTrip) => {
    setShoppingTrips((trips) => trips.map((trip) => (trip.id === tripId ? update(trip) : trip)));
    patchCachedShoppingTrip(tripId, update);
  }, []);

  const loadFinance = useCallback(async () => {
    if (!canUseFinance) return;
    const next = await loadFinanceData();
    setAccounts(next.accounts);
    setCategories(next.categories);
    setTransactions(next.transactions);
    setBudgets(next.budgets);
    setRecurringItems(next.recurringItems);
    setFinanceOverview(next.overview);
  }, [canUseFinance]);

  const reloadScanData = useCallback(async (token = scanToken) => {
    if (!token || !user) return;
    const next = await api<ScanData>(`/api/v1/qr/${token}`);
    setScanData(next);
  }, [scanToken, user]);

  // Everything the SHELL holds for a tab, and nothing else — no feature-refresh
  // dispatch (#1501). This is the arrival path: landing on a tab MOUNTS its
  // view, and a view that fetches for itself loads in that mount effect, so a
  // dispatch here would be a second, simultaneous copy of the same load. The
  // rule and the tab -> scope table live in app/tabRefresh.ts.
  //
  // Takes a `Tab` as well as a `RefreshScope` because it already resolves
  // `"active"` to one: the corrective-refresh drain below replays a scope that
  // was resolved that way, and a tab with no loader of its own (the assistant's
  // nonce aside) simply matches nothing here.
  const loadTabData = useCallback(async (scope: RefreshScope | Tab = "active") => {
    const target = scope === "active" ? tab : scope;
    if (target === "dashboard") await loadDashboard();
    if (target === "assistant") {
      // Bumping the nonce re-runs AssistantView's loads; it de-dupes the bump
      // that arrives while its own mount load is still in flight.
      setAssistantRefreshNonce((nonce) => nonce + 1);
      return;
    }
    if (target === "search") return;
    if (target === "inventory") await loadInventory();
    if (target === "assets") await loadAssets();
    if (target === "groceries") await loadGroceries();
    if (target === "chores") await loadChores();
    // Projects and the Home Map both render a view that fetches for itself, but
    // they also read shell state (`inventory` assets/locations, `chores`
    // occurrences), so these loaders stay on the arrival path. Their views'
    // own reads are dispatched by `refreshActiveTab` below, not from here.
    if (target === "projects") await Promise.all([loadInventory(), loadChores()]);
    if (target === "floor-plan") await loadInventory();
    // garden, bms, network and feedback have no shell loader at all: nothing in
    // App's own state holds one of their rows. On arrival their views load
    // themselves; on a refresh `dispatchSelfFetchingRefresh` asks them to.
    if (target === "recipes") await loadRecipes({ catalog: false });
    if (target === "meal-assembler") {
      // The Meals hub (#578) shows the planner alongside the assembler, so it
      // needs recipes + meal-plan entries (loadRecipes) and shopping lists for
      // the missing-ingredients bridge, not just assembler suggestions.
      await Promise.all([loadMealAssembler(), loadRecipes({ catalog: true }), loadShopping()]);
    }
    // Shopping needs the product catalog too (#635/#593/#594): the list
    // builder's definition picker, the save-as-product offer, and put-away
    // pre-fills all read `products`, which loadGroceries owns.
    if (target === "shopping") await Promise.all([loadShopping(), loadGroceries()]);
    if (target === "finance") await loadFinance();
    if (target === "settings") await loadMealAssembler();
    if (target === "scan") {
      await Promise.all([loadInventory(), loadGroceries()]);
      // A code nothing is labelled with is the scan page's own message
      // (September audit): the mount effect resolves the token and paints the
      // answer beside the scanner, so the refresh must not raise the same 404
      // a second time in the shell's band.
      await reloadScanData().catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) return;
        throw err;
      });
    }
  }, [
    loadAssets,
    loadDashboard,
    loadFinance,
    loadGroceries,
    loadChores,
    loadInventory,
    loadMealAssembler,
    loadRecipes,
    loadShopping,
    reloadScanData,
    tab
  ]);

  /**
   * Re-read what is on screen: the shell's loaders for this tab, plus the one
   * feature-refresh event its view listens on if the view fetches for itself.
   *
   * Every caller is a REFRESH — a realtime echo, the drain of a corrective
   * refresh, a post-write reload, a manual refresh action. Arrival is not one
   * of them and must call `loadTabData` instead (#1501): the view is mounting,
   * and its mount effect is its first load.
   */
  const refreshActiveTab = useCallback(async (scope: RefreshScope | Tab = "active") => {
    const target = scope === "active" ? tab : scope;
    await loadTabData(target);
    dispatchSelfFetchingRefresh(target);
  }, [loadTabData, tab]);

  const refreshAuthStatus = useCallback(async () => {
    const status = await api<AuthStatus>("/api/v1/auth/status");
    // Awaited, not fired and forgotten (codex review). `applyAuthStatus` below
    // puts the signed-out UI on screen, so a purge still in flight when it runs
    // leaves a window in which the next user can sign in and start loading
    // while the previous household's cached responses are still servable — the
    // leak this whole change is about, reached through a shared device rather
    // than through the cache surviving a sign-out. Ordinarily this is one
    // message round-trip to the worker.
    if (!status.user) await clearHouseholdData();
    applyAuthStatus(status);
  }, [applyAuthStatus, clearHouseholdData]);

  const applyRealtimeUpdates = useCallback(async () => {
    const shouldRefreshAuth = pendingAuthRefreshRef.current;
    pendingAuthRefreshRef.current = false;
    pendingRealtimeUpdatesRef.current = 0;
    pendingSilentRefreshRef.current = false;
    setPendingRealtimeUpdates(0);
    if (shouldRefreshAuth) await refreshAuthStatus();
    await refreshActiveTab("active");
  }, [refreshActiveTab, refreshAuthStatus]);

  useEffect(() => {
    applyRealtimeUpdatesRef.current = applyRealtimeUpdates;
  }, [applyRealtimeUpdates]);

  const scheduleRealtimeRefresh = useCallback(() => {
    if (realtimeRefreshTimerRef.current !== null) window.clearTimeout(realtimeRefreshTimerRef.current);
    realtimeRefreshTimerRef.current = window.setTimeout(() => {
      realtimeRefreshTimerRef.current = null;
      applyRealtimeUpdatesRef.current().catch((err) => {
        setError(err instanceof Error ? err.message : "Could not refresh household updates");
      });
    }, 650);
  }, []);

  // Open a mark cycle for a scope that is about to be refreshed *because of a
  // change this user made*, so the realtime echo of that same change can be
  // dropped (#520). Deliberately not wired into every refreshActiveTab call: a
  // plain tab-entry or manual refresh must not suppress the echo of a later
  // write that the surface is relying on the realtime path to pick up.
  //
  // The rules and the cycle's ordering live in app/selfRefresh.ts, where they
  // are tested; resolving "active" needs the live tab, so that stays here. It
  // is resolved once per cycle — which is also what refreshActiveTab does, so
  // the marks and the refresh cannot end up pointed at different scopes.
  const beginSelfRefresh = useCallback((scope: RefreshScope) => {
    const resolved = scope === "active" ? tabRef.current : scope;
    return beginSelfRefreshCycle(selfRefreshEchoRef.current, resolved);
  }, []);

  // Refresh after a write this app already performed outside run() — marks the
  // scope before the refresh so an echo arriving mid-flight is covered, and
  // again once it lands so the window is measured from fresh data.
  //
  // If the refresh itself fails the mark is retracted instead of re-armed: the
  // suppression is only earned by a refresh that actually happened, and the
  // realtime cursor has already advanced past the echo, so a surviving mark
  // would leave the scope stale until the user navigates (#520).
  const refreshAfterOwnChange = useCallback(async (scope: RefreshScope) => {
    // No commit() step: the write these callers are refreshing for has already
    // landed by the time they get here. It still has to be CLAIMED, though
    // (#800, codex review) — the refresh below covers it, and a write left
    // uncovered has its echo let through, which is a second refresh for every
    // direct-write surface and exactly the #520 behaviour that must not return.
    const cycle = beginSelfRefresh(scope);
    cycle.claim();
    try {
      await refreshActiveTab(scope);
      cycle.refreshed();
    } catch (err) {
      cycle.abandon();
      // Every caller fires this with `void`, so surface the failure here rather
      // than rejecting into an unhandled promise. The echo now allowed through
      // is what recovers the data.
      setError(err instanceof Error ? err.message : "Could not refresh after saving");
    }
  }, [beginSelfRefresh, refreshActiveTab, setError]);

  // A write by a surface that renders its own result and will NOT refetch —
  // the floor-plan editor (#1031). See `beginLocalWrite` in features/context.
  //
  // The cycle ordering is the same one `run()` uses, with the local view
  // standing in for the refresh: open before the write (the echo can arrive
  // while it is still in flight), `commit()` when it lands so the mark is
  // measured from the commit rather than from a possibly-slow start, then
  // `refreshed()` to seal — the screen already shows what the write did, which
  // is the thing a refresh would otherwise have gone and fetched.
  //
  // `abandon()` on failure is not optional: it releases the claim, so the echo
  // of a write that DID land behind a failed one is let through and refreshes
  // the screen. Suppressing it would leave the editor showing state the server
  // rejected, with nothing coming to correct it — the realtime cursor has
  // already advanced past the event.
  const beginLocalWrite = useCallback((scope: RefreshScope): LocalWriteCycle => {
    const cycle = beginSelfRefresh(scope);
    return {
      landed() {
        cycle.commit();
        cycle.refreshed();
      },
      abandon() {
        cycle.abandon();
      }
    };
  }, [beginSelfRefresh]);

  // Drain the corrective-refresh queue (#520). Deliberately a bare
  // `refreshActiveTab`, with no cycle around it: a corrective refresh that armed
  // a mark of its own would swallow the next legitimate echo — the exact hole it
  // exists to close — and because it opens no cycle it can never queue another
  // correction, so this cannot loop. The scopes come back already resolved,
  // which is why `refreshActiveTab` accepts a `Tab`.
  const applySelfRefreshCorrections = useCallback(() => {
    for (const scope of takeSelfRefreshCorrections(selfRefreshEchoRef.current)) {
      refreshActiveTab(scope as RefreshScope | Tab).catch((err) => {
        // Put the scope back before reporting: the echo this correction was
        // paying off is already consumed and will not replay, so a transient
        // failure here would otherwise strand the tab. requeue is bounded and
        // re-notifies, which is what schedules the next drain.
        requeueSelfRefreshCorrection(selfRefreshEchoRef.current, scope);
        setError(err instanceof Error ? err.message : "Could not refresh after saving");
      });
    }
  }, [refreshActiveTab, setError]);

  useEffect(() => {
    applySelfRefreshCorrectionsRef.current = applySelfRefreshCorrections;
  }, [applySelfRefreshCorrections]);

  // Is this event just the echo of a change run() already refreshed for? Only
  // the user's own edits qualify, only for the scope run() actually refreshed,
  // and only while that refresh is still fresh — an auth change or anyone else's
  // update always falls through to a real refresh (#520).
  const isOwnRefreshedEcho = useCallback((event: HouseholdRealtimeEvent) => {
    return isOwnRefreshedEchoFor(selfRefreshEchoRef.current, event, tabRef.current);
  }, []);

  const suppressRealtimeBanner = useCallback((durationMs = 2500) => {
    quietRealtimeUntilRef.current = Math.max(quietRealtimeUntilRef.current, Date.now() + durationMs);
  }, []);

  // Two shapes for back-compat: the original `showToast(msg, actions[])` and an
  // options object that can also carry the first-row deep link (#586).
  const showToast = useCallback((message: string, options: ToastAction[] | ToastOptions = []) => {
    if (!message) return;
    const { actions = [], link } = Array.isArray(options) ? { actions: options, link: undefined } : options;
    toastIdRef.current += 1;
    setToast({ id: toastIdRef.current, message, actions: actions.length ? actions : undefined, link });
  }, []);

  // Auto-dismiss lives in AppToast (#586): the timer has to pause on hover /
  // focus and while the marquee is scrolling, and only the component knows
  // whether the text overflowed.
  const dismissToast = useCallback(() => setToast(null), []);

  // Warn before leaving while background attachment uploads are still running,
  // so big files aren't lost by closing the tab mid-upload. Desktop only in
  // practice: mobile browsers do not fire beforeunload when the OS reclaims a
  // backgrounded tab.
  useEffect(() => {
    const warnOnPendingUploads = (event: BeforeUnloadEvent) => {
      if (backgroundUploads.pendingFileCount() <= 0) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnOnPendingUploads);
    return () => window.removeEventListener("beforeunload", warnOnPendingUploads);
  }, []);

  const run = useCallback(async (action: () => Promise<void>, options: RunOptions = {}) => {
    setBusy(true);
    setError("");
    // The refresh below already covers this mutation, so mark its scope as
    // self-refreshed and let the realtime handler drop the matching echo (#520).
    // Opened before the action runs, because the household event can be
    // delivered while the write is still in flight.
    const refreshScope = options.refresh === false ? null : options.refresh ?? "active";
    const cycle = refreshScope ? beginSelfRefresh(refreshScope) : null;
    try {
      await action();
      if (refreshScope) {
        // Re-arm the moment the write commits, before waiting on the refresh.
        // The echo lands ~900ms after the commit; anchoring the window here
        // instead of at the start of the action means a slow request (or a slow
        // refresh) can never let it slip past the mark.
        cycle?.commit();
        await refreshActiveTab(refreshScope);
        // Seals the cycle — but only if no later write to the same scope opened
        // its own cycle while this refresh was in flight. A superseded refresh
        // gives the scope up instead of vouching for it, because the data it
        // just painted may already be a write behind (see app/selfRefresh.ts).
        cycle?.refreshed();
      }
      if (options.successMessage) showToast(options.successMessage);
      return true;
    } catch (err) {
      // Nothing here got the refresh it marked for — either the action threw
      // (no write, so nothing to echo) or the write committed and
      // refreshActiveTab threw, which leaves stale data on screen. Either way
      // the echo has to be allowed through, so the cycle releases the scope.
      // Only its own mark: a concurrent write on the same scope keeps its
      // window, because the token will not match.
      cycle?.abandon();
      // A demo instance refusing to persist is expected, not a failure: surface
      // the API's own wording as a toast so the red error band stays reserved
      // for things that actually went wrong (#839).
      if (isDemoModeRejection(err)) {
        showToast(err instanceof Error ? err.message : "Changes aren't saved on the demo.");
        return false;
      }
      // The sink for every translated failure above — including the CSV import
      // codes and the Google sign-in codes, which arrive already resolved. Its
      // own last resort is the key lib/api.ts already uses for the same
      // sentence, so a non-Error rejection does not land here in English.
      setError(err instanceof Error ? err.message : copy("errors.actionFailed", "Action failed"));
      return false;
    } finally {
      setBusy(false);
    }
  }, [beginSelfRefresh, refreshActiveTab, showToast]);

  const occurrenceUndoToastActions = useCallback((
    occurrences: Array<{ id: EntityReferenceId }>,
    reason: string,
    refresh: RefreshScope,
    removeOccurrenceIds: EntityReferenceId[] = []
  ): ToastAction[] => {
    const ids = occurrenceIds(occurrences);
    // "View" used to lead this list; every caller now passes a first-row link
    // that goes somewhere more precise (the chore itself, not the tab), so the
    // button would be a second, worse copy of it (#586).
    return [
      {
        label: "Undo",
        onClick: () => {
          if (!ids.length) return;
          void (async () => {
            let reopened = 0;
            const undone = await run(async () => {
              // A completion generates at most one successor per occurrence, so
              // there are never more removals than occurrences and every removal
              // batch has an occurrence batch to ride along with.
              const removeBatches = occurrenceBatches(removeOccurrenceIds);
              const batches = occurrenceBatches(ids);
              for (const [index, batch] of batches.entries()) {
                await postJson("/api/v1/task-occurrences/batch/reopen", {
                  occurrenceIds: batch,
                  removeOccurrenceIds: removeBatches[index] ?? [],
                  reason
                });
                reopened += batch.length;
              }
            }, { refresh, successMessage: "Action undone" });
            // Same partial-commit exposure as the forward actions below: an
            // early reopen can land before a later one fails, and `run` skips
            // its refresh on the failure path, so the panel would keep showing
            // rows that are already back. Best effort — whatever knocked the
            // request out usually takes this with it, and `run` has already put
            // the real error on screen.
            if (!undone && reopened) await refreshActiveTab(refresh).catch(() => {});
          })();
        }
      }
    ];
  }, [refreshActiveTab, run]);

  // A chunked batch is not atomic end to end: request N can commit before
  // request N+1 fails. `run` reports the failure and skips its refresh, so this
  // collects the ids the server actually reported back, refreshes for the part
  // that landed, and hands the caller exactly that subset to name in its toast
  // and to target with Undo. Without it a partial run reads as a clean success
  // and Undo would reopen occurrences that were never touched.
  const runOccurrenceBatches = useCallback(async (
    occurrences: TaskOccurrence[],
    send: (batch: EntityReferenceId[]) => Promise<{ updated?: TaskOccurrence[]; generatedOccurrenceIds?: EntityReferenceId[] }>
  ): Promise<{ applied: TaskOccurrence[]; generatedOccurrenceIds: EntityReferenceId[]; requested: number }> => {
    const ids = occurrenceIds(occurrences);
    const appliedIds = new Set<string>();
    const generatedOccurrenceIds: EntityReferenceId[] = [];
    const settled = await run(async () => {
      for (const batch of occurrenceBatches(ids)) {
        const result = await send(batch);
        for (const id of result.updated?.map((occurrence) => occurrence.id) ?? batch) appliedIds.add(String(id));
        generatedOccurrenceIds.push(...(result.generatedOccurrenceIds ?? []));
      }
    }, { refresh: "dashboard" });
    const applied = occurrences.filter((occurrence) => appliedIds.has(String(occurrence.id)));
    // Best effort, and deliberately swallowed: whatever knocked the request out
    // usually takes this with it, `run` has already put the real error on
    // screen, and letting it throw would cost the caller the very toast and
    // Undo this helper exists to hand back for the batches that did commit.
    if (!settled && applied.length) await refreshActiveTab("dashboard").catch(() => {});
    return { applied, generatedOccurrenceIds, requested: ids.length };
  }, [refreshActiveTab, run]);

  const completeTaskOccurrence = useCallback(async (occurrence: TaskOccurrence) => {
    let completed = false;
    let generatedOccurrenceIds: EntityReferenceId[] = [];
    await run(async () => {
      const result = await postJson<{ generatedOccurrenceIds?: EntityReferenceId[] }>(`/api/v1/task-occurrences/${occurrence.id}/complete`, {});
      generatedOccurrenceIds = result.generatedOccurrenceIds ?? [];
      completed = true;
    }, { refresh: "dashboard" });
    if (completed) {
      showToast(`${occurrence.task.title} done`, {
        actions: occurrenceUndoToastActions([occurrence], "Undo dashboard completion", "dashboard", generatedOccurrenceIds),
        link: choresListLink
      });
    }
  }, [run, showToast, occurrenceUndoToastActions]);

  // "Done all" / "Snooze all" on the dashboard priority panel (#822). Both act on
  // every occurrence the panel lists, including the ones folded into a grouped
  // recurring card and the ones the mobile row clamp hides, so the button label
  // carries the real count.
  const completeTaskOccurrences = useCallback(async (occurrences: TaskOccurrence[]) => {
    const { applied, generatedOccurrenceIds, requested } = await runOccurrenceBatches(occurrences, (batch) => (
      postJson("/api/v1/task-occurrences/batch/complete", { occurrenceIds: batch, notes: "Completed from dashboard" })
    ));
    if (!applied.length) return;
    // Keyed off what applied, not off whether `run` succeeded: `run` also
    // reports false when every write landed but its refresh threw, and that is
    // a clean completion as far as the user's data is concerned.
    const message = applied.length === requested
      ? `Completed ${applied.length} ${pluralize(applied.length, "task")}`
      : `Completed ${applied.length} of ${requested} ${pluralize(requested, "task")} before the request failed`;
    showToast(message, { actions: occurrenceUndoToastActions(applied, "Undo dashboard bulk completion", "dashboard", generatedOccurrenceIds), link: choresListLink });
  }, [runOccurrenceBatches, showToast, occurrenceUndoToastActions]);

  const snoozeTaskOccurrence = useCallback(async (occurrence: TaskOccurrence) => {
    let snoozed = false;
    await run(async () => {
      await postJson(`/api/v1/task-occurrences/${occurrence.id}/snooze`, {
        snoozedUntil: nextSnoozeDate(occurrence.effectiveDueDate),
        reason: "Snoozed from dashboard"
      });
      snoozed = true;
    }, { refresh: "dashboard" });
    if (snoozed) {
      showToast(`${occurrence.task.title} snoozed`, {
        actions: occurrenceUndoToastActions([occurrence], "Undo dashboard snooze", "dashboard"),
        link: choresListLink
      });
    }
  }, [run, showToast, occurrenceUndoToastActions]);

  const snoozeTaskOccurrences = useCallback(async (occurrences: TaskOccurrence[]) => {
    // Per-row snooze pushes each occurrence to nextSnoozeDate(its own due date).
    // Every occurrence the priority panel lists is overdue or due today, so that
    // resolves to tomorrow for all of them and one shared date is equivalent;
    // deriving it from the latest due date keeps that true even if a future-dated
    // occurrence ever reaches the panel.
    const latestDueDate = occurrences.reduce((latest, occurrence) => (occurrence.effectiveDueDate > latest ? occurrence.effectiveDueDate : latest), "");
    const { applied, requested } = await runOccurrenceBatches(occurrences, (batch) => (
      postJson("/api/v1/task-occurrences/batch/snooze", {
        occurrenceIds: batch,
        snoozedUntil: nextSnoozeDate(latestDueDate),
        reason: "Snoozed from dashboard"
      })
    ));
    if (!applied.length) return;
    const message = applied.length === requested
      ? `Snoozed ${applied.length} ${pluralize(applied.length, "task")}`
      : `Snoozed ${applied.length} of ${requested} ${pluralize(requested, "task")} before the request failed`;
    showToast(message, { actions: occurrenceUndoToastActions(applied, "Undo dashboard bulk snooze", "dashboard"), link: choresListLink });
  }, [runOccurrenceBatches, showToast, occurrenceUndoToastActions]);

  const catchUpTaskOccurrences = useCallback(async (occurrences: TaskOccurrence[]) => {
    const { applied, generatedOccurrenceIds, requested } = await runOccurrenceBatches(occurrences, (batch) => (
      postJson("/api/v1/task-occurrences/batch/skip", {
        occurrenceIds: batch,
        skippedAt: new Date().toISOString(),
        reason: "Caught up missed occurrences from dashboard"
      })
    ));
    if (!applied.length) return;
    const message = applied.length === requested
      ? `Skipped ${applied.length} missed ${pluralize(applied.length, "occurrence")}`
      : `Skipped ${applied.length} of ${requested} missed ${pluralize(requested, "occurrence")} before the request failed`;
    showToast(message, { actions: occurrenceUndoToastActions(applied, "Undo dashboard catch-up", "dashboard", generatedOccurrenceIds), link: choresListLink });
  }, [runOccurrenceBatches, showToast, occurrenceUndoToastActions]);

  const skipTaskOccurrences = useCallback(async (occurrences: TaskOccurrence[]) => {
    const { applied, generatedOccurrenceIds, requested } = await runOccurrenceBatches(occurrences, (batch) => (
      postJson("/api/v1/task-occurrences/batch/skip", {
        occurrenceIds: batch,
        skippedAt: new Date().toISOString(),
        reason: "Skipped due occurrences from dashboard"
      })
    ));
    if (!applied.length) return;
    const message = applied.length === requested
      ? `Skipped ${applied.length} ${pluralize(applied.length, "occurrence")}`
      : `Skipped ${applied.length} of ${requested} ${pluralize(requested, "occurrence")} before the request failed`;
    showToast(message, { actions: occurrenceUndoToastActions(applied, "Undo dashboard skip", "dashboard", generatedOccurrenceIds), link: choresListLink });
  }, [runOccurrenceBatches, showToast, occurrenceUndoToastActions]);

  const {
    createChore,
    uploadChoreAttachments,
    updateChore,
    deleteChore,
    completeChoreOccurrence,
    snoozeChoreOccurrence,
    skipChoreOccurrence,
    reopenChoreOccurrence,
    applyChoreTemplate,
    openChoreTimeline
  } = useChoreMutations(choresState, { run, showToast, setError, refreshAfterOwnChange, occurrenceUndoToastActions });

  const clearAssetDetails = useCallback(() => {
    setSelectedAsset(null);
    setAssetNotFound(false);
    setAssetLoadError(null);
    setAssetMovements([]);
    setAssetAttachments([]);
  }, []);

  const retryAssetLoad = useCallback(() => {
    setAssetLoadError(null);
    setAssetLoadAttempt((attempt) => attempt + 1);
  }, []);

  const closeAssetDetails = useCallback(() => {
    clearAssetDetails();
    setAssetRouteId(null);
    if (assetIdFromPath(window.location.pathname)) navigateBrowser(tabPath("inventory"), true);
  }, [clearAssetDetails]);

  // #980: opening a place is exclusive with the asset detail surface, so it
  // clears both the route id and the loaded asset (selectedAsset alone keeps
  // the detail pane alive — see InventoryView's hasAssetDetail).
  const openPlaceDetails = useCallback((placeId: EntityReferenceId, options: { replace?: boolean } = {}) => {
    setActiveTab("inventory");
    setRouteNotFound(false);
    setScanData(null);
    setAssetRouteId(null);
    clearAssetDetails();
    setPlaceRouteId(placeId);
    navigateBrowser(placeDetailsPath(placeId), options.replace ?? false);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [clearAssetDetails]);

  // Replaces by default, like closeAssetDetails, so the page's own back button
  // does not leave a dead entry. The Add-thing hand-off passes replace: false
  // so browser Back returns to the place.
  const closePlaceDetails = useCallback((options: { replace?: boolean } = {}) => {
    setPlaceRouteId(null);
    if (placeIdFromPath(window.location.pathname)) navigateBrowser(tabPath("inventory"), options.replace ?? true);
  }, []);

  const openRecipeDetails = useCallback((recipeId: EntityReferenceId) => {
    setActiveTab("recipes");
    setRouteNotFound(false);
    setRecipeRouteId(recipeId);
    navigateBrowser(recipeDetailsPath(recipeId));
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const closeRecipeDetails = useCallback(() => {
    setRecipeRouteId(null);
    if (recipeIdFromPath(window.location.pathname)) navigateBrowser(tabPath("recipes"), true);
  }, []);

  const authStartupFailed = useAuthStartup(authProbe, applyAuthStatus, clearHouseholdData,
    setAuthReady, setAuthUnreachable, setError);

  // The connection coming back is the retry nobody has to press. The timer
  // covers the cases the browser never announces — a captive portal, a proxy
  // that answered 502 for a while — so the screen never becomes permanent.
  useEffect(() => {
    if (!authUnreachable) return undefined;
    const retry = () => setAuthProbe((count) => count + 1);
    window.addEventListener("online", retry);
    const timer = window.setInterval(retry, 20_000);
    return () => {
      window.removeEventListener("online", retry);
      window.clearInterval(timer);
    };
  }, [authUnreachable]);

  // The public pages are a light surface whatever the visitor prefers, and the
  // app goes straight back to that preference on the way out. This has to be an
  // effect of App rather than of MarketingSite: a child's effects run first, so
  // the auth-status theme would overwrite the light one on the same commit.
  useEffect(() => {
    // identity is a dependency because signing in is what changes the answer:
    // the household member's own preference arrives after the first paint, and
    // a public page must not be repainted with it.
    if (onPublicPage) applyPublicPageTheme();
    else applyThemePreference(identity ? normalizeThemePreference(identity.themePreference) : readStoredThemePreference());
  }, [onPublicPage, identity]);

  // One resolved locale drives every formatter and the translation runtime
  // (#1088). Recomputed when identity changes, because the signed-in user's
  // stored preference arrives after the first paint; before that, and on the
  // public pages, the browser's own languages decide.
  const resolvedLocale: ResolvedLocale = useMemo(
    () => resolveLocale(identity?.languagePreference),
    [identity?.languagePreference]
  );
  const i18nStartup = useI18nStartup(resolvedLocale, authReady);
  const i18nReady = i18nStartup.status === "ready";
  useStartupStages(publicRoute ? "public" : !user ? "signin" : tab, resolvedLocale, authReady,
    i18nStartup.status, i18nStartup.error, authUnreachable, !user && !publicRoute);

  // Mirrored out of React so the formatters in app/locale.ts — and the helpers
  // that format outside a render, like money() — can read the locale without
  // every call site threading it through.
  //
  // Publishing it correctly took two attempts, and both failure modes matter:
  //
  //   In a plain effect, children have ALREADY rendered against the old value,
  //   and nothing re-renders them afterwards — the views that only format dates
  //   (Network, BMS, Releases, Finance) never call useTranslation, so i18next's
  //   languageChanged event does not reach them. Dates stayed in the previous
  //   language until something unrelated happened to re-render.
  //
  //   During render, it is a write to module state from a render pass that
  //   React is free to abandon. A preempted render would leave the global set
  //   to a locale that never committed, and the next independent re-render
  //   would format against it.
  //
  // So: publish in a LAYOUT effect, which only runs for a render that actually
  // committed, and record it in state so that publishing re-renders the tree
  // that reads it. Layout effects run before paint, so the corrected pass is
  // what the user sees — no flash of the previous language.
  const [committedLocale, setCommittedLocale] = useState<ResolvedLocale>(getResolvedLocale);
  useLayoutEffect(() => {
    if (committedLocale === resolvedLocale) return;
    setResolvedLocale(resolvedLocale);
    setCommittedLocale(resolvedLocale);
  }, [committedLocale, resolvedLocale]);


  // Paint hint for the next cold load, so the boot script knows whether "/" is
  // the landing page or the Dashboard before any of this has run.
  useEffect(() => {
    rememberSessionHint(hasSession);
  }, [hasSession]);

  // This PR lifts the palette's open state above every signed-out and
  // no-household early return, so it now outlives the session that opened it:
  // an expiring auth check would drop the user on the landing page with the
  // overlay still up, and a later sign-in would inherit it.
  // Keyed on `user`, not `hasSession`. `hasSession` stays true for the
  // identity-without-household state, so an auth refresh that removes the
  // active membership clears the household data and unmounts the palette while
  // leaving this state open — and the palette would then reappear, already
  // open and stealing focus, as soon as that identity joined or created a
  // household. `!user` covers the signed-out path and that one.
  useEffect(() => {
    if (!user) setPaletteOpen(false);
  }, [user]);

  useEffect(() => {
    tabRef.current = tab;
  }, [tab]);

  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);

  /**
   * Adopt whatever route the URL currently describes. Used by the Back/Forward
   * buttons and by any in-page navigation that can land back INSIDE the app —
   * the shell reads its tab from the URL, so a navigation that moved the URL
   * without this would leave the previous tab on screen under an address that
   * says something else.
   */
  const adoptRouteFromLocation = useCallback(() => {
    const nextRoute = currentRoute();
    setActiveTab(nextRoute.tab);
    setRouteNotFound(nextRoute.notFound);
    setScanToken(nextRoute.scanToken);
    setAssetRouteId(nextRoute.assetId);
    setPlaceRouteId(nextRoute.placeId);
    setProductRouteId(nextRoute.productId);
    setRecipeRouteId(nextRoute.recipeId);
    setAssistantChatRouteId(nextRoute.assistantChatId);
    setReleaseNotesVersion(nextRoute.releaseNotesVersion);
    if (!nextRoute.assetId) clearAssetDetails();
    if (nextRoute.tab !== "scan" || !nextRoute.scanToken) setScanData(null);
    setPublicPath(window.location.pathname);
  }, [clearAssetDetails]);

  useEffect(() => {
    window.addEventListener("popstate", adoptRouteFromLocation);
    return () => window.removeEventListener("popstate", adoptRouteFromLocation);
  }, [adoptRouteFromLocation]);

  useEffect(() => {
    if (!activeUserId || !activeHouseholdId) return;
    // Drop any stale banner (e.g. a transient 502/504 gateway error from a prior
    // screen) whenever the active tab changes, so it doesn't persist across
    // navigation (#541). The reload below re-sets a fresh error only if it fails.
    setError("");
    let cancelled = false;
    // `loadTabData`, not `refreshActiveTab` (#1501): arriving at a tab mounts
    // its view, and a view that fetches for itself has already started its own
    // load by the time this effect commits. Dispatching its scope here as well
    // ran that load a second time, simultaneously.
    loadTabData("active").catch((err) => {
      // A refresh started for the tab we just left must not repopulate the
      // banner we cleared on navigation; only the current tab's failure counts.
      if (cancelled) return;
      setError(err instanceof Error ? err.message : "Could not load data");
    });
    return () => {
      cancelled = true;
    };
  }, [activeHouseholdId, activeUserId, loadTabData, tab]);

  // Which of the shell's date-derived aggregates the screen in front of the
  // reader is showing — the pair on the dashboard, the rollup on Things, and
  // nothing anywhere else. `null` unsubscribes, so a reader parked on Groceries
  // is not fetching value reports at midnight, and this only changes identity
  // when the answer changes rather than on every tab move.
  const dateDerivedScope: DateDerivedScope | null = tab === "dashboard" ? "dashboard" : tab === "inventory" ? "inventory" : null;

  // The aggregate value and coverage answers follow the reader's own day, the
  // way the depreciation panel and the ages section beside them already do
  // (#1450). Without this the two disagree from local midnight until the next
  // navigation: the panel has re-derived, the totals above it have not (#1469).
  //
  // Applied straight into state, not through `refreshActiveTab`: this is a
  // silent replacement of two figures, not a reload of the screen, and the six
  // other reads in `loadDashboardData` have no reason to run again.
  useEffect(() => {
    if (!activeUserId || !activeHouseholdId || !dateDerivedScope) return;
    return subscribeToDateDerivedAggregates(dateDerivedScope, (next) => {
      // On the Things screen this hands a new `assetValue` to the ages and
      // forecast sections, which treat its identity as a reload seam, so they
      // ask once more than the boundary strictly needs. That is the same one
      // extra read every tab entry already costs them, it is not one of this
      // loader's reads, and both sections subscribe to the boundary themselves
      // — deduping here would make state identity depend on deep equality to
      // save a cheap GET.
      setAssetValue(next.assetValue);
      // Only the dashboard scope asks for coverage; leaving it alone on Things
      // is what keeps a screen from fetching a report it does not render.
      if (next.insuranceCoverage) setInsuranceCoverage(next.insuranceCoverage);
    });
  }, [activeHouseholdId, activeUserId, dateDerivedScope]);

  // No separate chores loader here: `refreshActiveTab` already calls
  // loadChores() for the chores tab, and its identity changes with choreView, so
  // the effect above re-runs on both tab entry and view change. A second effect
  // doubled every chores load — two full context/occurrence/template/analytics
  // sets, and two category seeds because both raced the once-per-session ref
  // (#520).

  useEffect(() => {
    if (!user || (tab !== "meal-assembler" && tab !== "settings")) return;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      loadMealAssembler(controller.signal).catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : "Could not load meals");
      });
    }, 350);
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [loadMealAssembler, tab, user]);

  useEffect(() => {
    setScanLoadError(null);
    if (!scanToken || !user) return;
    (async () => {
      // The code is what the person is waiting on, so it resolves first and
      // the inventory and food loads the result page needs follow. The old
      // order ran the two big loads first, and a "not found" arrived many
      // seconds after the address had already changed (September audit).
      try {
        await reloadScanData();
      } catch (err) {
        setScanData(null);
        // A code nothing is labelled with is the scan page's own message,
        // beside the input, not the shell's red band.
        setScanLoadError(err instanceof ApiError && err.status === 404 ? "not-found" : err instanceof Error ? err.message : "Could not load scan");
        return;
      }
      await Promise.all([loadInventory(), loadGroceries()]);
    })().catch((err) => {
      setError(err instanceof Error ? err.message : "Could not load scan");
    });
  }, [loadGroceries, loadInventory, reloadScanData, scanToken, user]);

  useEffect(() => {
    const clearReconnectTimer = () => {
      if (realtimeReconnectTimerRef.current !== null) {
        window.clearTimeout(realtimeReconnectTimerRef.current);
        realtimeReconnectTimerRef.current = null;
      }
    };
    const clearReopenTimer = () => {
      if (realtimeReopenTimerRef.current !== null) {
        window.clearTimeout(realtimeReopenTimerRef.current);
        realtimeReopenTimerRef.current = null;
      }
    };
    const markConnected = () => {
      clearReconnectTimer();
      clearReopenTimer();
      realtimeOutageSinceRef.current = null;
      realtimeReopenAttemptsRef.current = 0;
      setRealtimeState("connected");
    };

    if (!activeUserId || !activeHouseholdId) {
      clearReconnectTimer();
      clearReopenTimer();
      realtimeOutageSinceRef.current = null;
      realtimeReopenAttemptsRef.current = 0;
      setRealtimeState("connected");
      pendingRealtimeUpdatesRef.current = 0;
      pendingSilentRefreshRef.current = false;
      pendingAuthRefreshRef.current = false;
      setPendingRealtimeUpdates(0);
      return undefined;
    }

    const cursor = readRealtimeCursor(activeUserId, activeHouseholdId);
    const url = new URL("/api/v1/household/events", window.location.origin);
    if (cursor) url.searchParams.set("since", String(cursor));
    const source = new EventSource(`${url.pathname}${url.search}`);

    // Only surface "reconnecting" if the connection stays down past the grace
    // window. A blip that reconnects before the timer fires never shows the
    // banner; onopen/`connected` clear a pending timer (#458). The window runs
    // from the start of the outage, so the reopens below cannot keep pushing it
    // out (#574).
    const scheduleReconnecting = () => {
      if (realtimeOutageSinceRef.current === null) realtimeOutageSinceRef.current = Date.now();
      if (realtimeReconnectTimerRef.current !== null) return;
      const remaining = remainingReconnectGraceMs(realtimeOutageSinceRef.current, Date.now());
      if (remaining <= 0) {
        setRealtimeState("reconnecting");
        return;
      }
      realtimeReconnectTimerRef.current = window.setTimeout(() => {
        realtimeReconnectTimerRef.current = null;
        setRealtimeState("reconnecting");
      }, remaining);
    };

    // EventSource retries by itself only while `readyState` is CONNECTING. A
    // non-2xx status or a non-`text/event-stream` content type — an edge serving
    // an error page, a 401 because the session cookie never reached the route —
    // is fatal: readyState CLOSED, one `error`, and no further attempt ever.
    // Nothing else in this app rebuilds the source, so that left the session
    // permanently without live updates and the banner permanently pinned (#574).
    const handleRealtimeError = () => {
      const plan = planRealtimeError({
        readyState: source.readyState,
        failedAttempts: realtimeReopenAttemptsRef.current
      });
      scheduleReconnecting();
      if (plan.reopenInMs === null) return;
      // Guard before counting the attempt: a second `error` on an already-dead
      // source must not advance the backoff past a reopen that is still pending.
      if (realtimeReopenTimerRef.current !== null) return;
      realtimeReopenAttemptsRef.current += 1;
      realtimeReopenTimerRef.current = window.setTimeout(() => {
        realtimeReopenTimerRef.current = null;
        // Re-runs this effect, which closes the dead source and opens a new one
        // from the stored cursor, so nothing recorded during the outage is lost.
        setRealtimeEpoch((epoch) => epoch + 1);
      }, plan.reopenInMs);
    };

    // A reopen mid-outage starts with the banner clock already running: this
    // attempt has not connected yet, and only an open may say otherwise.
    if (realtimeOutageSinceRef.current !== null) scheduleReconnecting();

    function bumpPending({ silent = false }: { silent?: boolean } = {}) {
      // Inside a quiet window (the user's own in-progress edits), or for a change
      // this user made themselves, refresh context silently instead of raising the
      // layout-shifting "updates available" banner.
      if (silent || Date.now() < quietRealtimeUntilRef.current) {
        // Remember there is unapplied work even while hidden or busy: this event
        // is already consumed and will not replay.
        pendingSilentRefreshRef.current = true;
        if (!document.hidden && !busyRef.current) scheduleRealtimeRefresh();
        return;
      }
      pendingRealtimeUpdatesRef.current = Math.min(pendingRealtimeUpdatesRef.current + 1, 99);
      setPendingRealtimeUpdates(pendingRealtimeUpdatesRef.current);
      if (!document.hidden && !busyRef.current) scheduleRealtimeRefresh();
    }

    function parseEvent(event: MessageEvent): HouseholdRealtimeEvent | null {
      try {
        return JSON.parse(event.data) as HouseholdRealtimeEvent;
      } catch {
        return null;
      }
    }

    source.onopen = () => markConnected();
    source.onerror = () => handleRealtimeError();
    source.addEventListener("connected", (event) => {
      const payload = parseEvent(event);
      rememberRealtimeCursor(activeUserId, activeHouseholdId, payload?.cursor);
      markConnected();
      // A stream opened WITHOUT a cursor starts at the server's current head,
      // so anything recorded between this session's /auth/status call and the
      // stream opening is never delivered — it is not replayed, because there
      // is no point to replay from. A reconnect does not have this problem: it
      // resumes from the stored cursor.
      //
      // The gap is small and easy to land in. The Google picture import (#1056)
      // is detached from sign-in and commits into exactly this window on a slow
      // download, leaving the tab that just signed in showing a monogram until
      // it was reloaded. A rename or a permission change made elsewhere in the
      // same moment was lost the same way.
      //
      // One silent re-read of the identity closes it, for every targeted event
      // rather than for the one that exposed it.
      if (!cursor) {
        pendingAuthRefreshRef.current = true;
        bumpPending({ silent: true });
      }
    });
    source.addEventListener("household-change", (event) => {
      const payload = parseEvent(event);
      if (!payload) return;
      rememberRealtimeCursor(activeUserId, activeHouseholdId, payload.id);
      if (payload.authChanged) pendingAuthRefreshRef.current = true;
      // Before the tab filter, deliberately (#1207): a component-local fetch
      // can be mounted on a tab the event does not name — the maintenance-spend
      // report is on the finance overview as well as on Things — and the filter
      // below would drop the only notice it was ever going to get. The shell's
      // own loads still happen only for the active tab, which is what that
      // filter is for.
      if (payload.scopes?.includes("inventory")) dispatchFeatureRefresh("inventory");
      if (!eventAffectsTab(payload, tabRef.current)) return;
      if (isOwnRefreshedEcho(payload)) return;
      // `self` means this user authored the change. The echo-suppression above
      // only drops it when the write went through run() and armed a mark for the
      // scope on screen, so writes from other paths still surfaced a banner
      // announcing the user's own edits back to them (#932). The data still
      // refreshes — only the "N household updates available" count is withheld,
      // because a count of your own edits is never something to act on. An agent
      // tool run reports self=false and keeps the banner.
      bumpPending({ silent: payload.self === true && !payload.authChanged });
    });
    source.addEventListener("auth-change", (event) => {
      const payload = parseEvent(event);
      if (payload?.refreshAuth !== false) {
        pendingAuthRefreshRef.current = true;
        bumpPending();
      }
    });
    source.addEventListener("stream-error", () => {
      pendingAuthRefreshRef.current = true;
      bumpPending();
    });

    function refreshWhenVisible() {
      if (document.hidden || busyRef.current) return;
      if (pendingRealtimeUpdatesRef.current > 0 || pendingSilentRefreshRef.current) scheduleRealtimeRefresh();
    }
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      clearReconnectTimer();
      clearReopenTimer();
      source.close();
    };
    // `realtimeEpoch` is the reopen trigger and is deliberately read for its
    // identity alone (#574).
  }, [activeHouseholdId, activeUserId, isOwnRefreshedEcho, realtimeEpoch, scheduleRealtimeRefresh]);

  useEffect(() => {
    if (busy || document.hidden) return;
    if (pendingRealtimeUpdatesRef.current < 1 && !pendingSilentRefreshRef.current) return;
    scheduleRealtimeRefresh();
  }, [busy, scheduleRealtimeRefresh, pendingRealtimeUpdates]);

  useEffect(() => () => {
    if (realtimeRefreshTimerRef.current !== null) window.clearTimeout(realtimeRefreshTimerRef.current);
    if (selfRefreshCorrectionTimerRef.current !== null) window.clearTimeout(selfRefreshCorrectionTimerRef.current);
  }, []);

  // Offline outbox: mirror queued-write count into state so the shell can show a
  // "waiting to sync" indicator with a retry control.
  useEffect(() => subscribePendingCount(setPendingSyncCount), []);

  // Background attachment uploads (#522): same idea, so the shell can show that
  // files are still going up after the create screen has navigated away.
  useEffect(() => backgroundUploads.subscribe(setPendingUploadCount), []);

  // Track connectivity and flush the outbox whenever we regain it. The 'online'
  // event and window focus are the cross-browser foreground replay path (the
  // Background Sync tag registered in offlineQueue.ts is a Chromium-only bonus).
  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      void flushQueue();
    };
    const handleOffline = () => setOnline(false);
    const handleFocus = () => {
      if (navigator.onLine !== false) void flushQueue();
    };
    setOnline(navigator.onLine !== false);
    if (navigator.onLine !== false) void flushQueue();
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  const retryOfflineQueue = useCallback(() => {
    void flushQueue();
  }, []);

  const handleScanToken = useCallback((token: string) => {
    setScanToken(token);
    setActiveTab("scan");
    setRouteNotFound(false);
    setAssetRouteId(null);
    setPlaceRouteId(null);
    clearAssetDetails();
    navigateBrowser(tabPath("scan", token));
  }, [clearAssetDetails]);

  const navigateToTab = useCallback((nextTab: Tab) => {
    setActiveTab(nextTab);
    setRouteNotFound(false);
    if (nextTab !== "scan" || !scanToken) setScanData(null);
    setAssetRouteId(null);
    setPlaceRouteId(null);
    setProductRouteId(null);
    setRecipeRouteId(null);
    setReleaseNotesVersion(null);
    clearAssetDetails();
    if (nextTab === "assistant") {
      // Tab-restore (#603): an in-app switch back to the Assistant reopens the
      // chat that was open when the tab was left, with the URL showing it.
      // Only this in-app path restores — a typed/reloaded bare /assistant URL
      // is always the new-chat page (the URL is the source of truth). A
      // pending seed handoff (#615) starts its own fresh conversation, so it
      // lands on bare /assistant instead of the restored chat.
      const storedChatId = activeHouseholdId && !readAssistantSeed(activeHouseholdId)
        ? readStoredAssistantChatId(activeHouseholdId)
        : null;
      setAssistantChatRouteId(storedChatId);
      navigateBrowser(storedChatId ? assistantChatPath(storedChatId) : tabPath("assistant"));
    } else {
      setAssistantChatRouteId(null);
      navigateBrowser(tabPath(nextTab, scanToken));
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [activeHouseholdId, clearAssetDetails, scanToken]);

  // Stable so the header's search button never re-renders on it (#1055).
  const openCommandPalette = useCallback(() => setPaletteOpen(true), []);

  // AssistantView reports session switches here so the URL and App route state
  // stay in step: replace when stamping a just-created chat's uuid (#603 — no
  // Back entry to the empty composer), push when moving between chats.
  const handleAssistantChatRoute = useCallback((chatId: string | null, options: { replace?: boolean } = {}) => {
    setAssistantChatRouteId(chatId);
    navigateBrowser(chatId ? assistantChatPath(chatId) : tabPath("assistant"), options.replace ?? false);
  }, []);


  const openAssetDetails = useCallback(async (asset: Asset, options: { navigate?: boolean; replace?: boolean } = {}) => {
    const shouldNavigate = options.navigate ?? true;
    if (shouldNavigate) {
      setActiveTab("inventory");
      setRouteNotFound(false);
      setScanData(null);
      setAssetRouteId(asset.id);
      // #980: the two inventory detail surfaces are exclusive.
      setPlaceRouteId(null);
      navigateBrowser(assetDetailsPath(asset.id), options.replace ?? false);
      if (window.matchMedia("(max-width: 980px)").matches) window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    setSelectedAsset(asset);
    setAssetNotFound(false);
    setAssetLoadError(null);
    assetNotFoundIdRef.current = null;
    setError("");
    try {
      const [movements, attachments] = await Promise.all([
        api<AssetMovement[]>(`/api/v1/assets/${asset.id}/movements`),
        api<Attachment[]>(`/api/v1/assets/${asset.id}/attachments`)
      ]);
      setAssetMovements(movements);
      setAssetAttachments(attachments);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load asset details");
    }
  }, []);

  /** Detached asset photo/file upload (#733, phase 2 of #522).
   *
   * The reorder is the reason this could not be a mechanical swap. The server
   * assigns collectionOrder under a row lock now (#734), but it still cannot
   * know which file the user picked as the cover, so every asset upload path
   * followed its upload with an explicit reorder. Backgrounding the upload
   * without moving that reorder would land photos in id order with an arbitrary
   * cover — so it moves into `finalize`, which runs after every file settles
   * and before anything is reported. */
  const uploadAssetAttachments = useCallback((input: {
    assetId: EntityReferenceId;
    files: File[];
    /** Index into `files` of the photo the user chose as cover. */
    primaryPhotoIndex?: number | null;
    /** Photos already on the asset, kept ahead of the new ones in the order. */
    existingPhotoIds?: number[];
    /** Cover to keep when this upload contributes no photo of its own. */
    fallbackPrimaryAttachmentId?: number | null;
    /** Names the asset in the failure toast. */
    label: string;
    /** What these files ARE (#1176). Defaults to the same `other` an
     * undeclared upload has always become, so the photo paths are unchanged;
     * the Documents & manuals section passes the kind the member chose, and a
     * document that declared nothing is refused by the server. */
    collectionType?: AttachmentCollectionType;
    /** Runs once the photos are ordered, with what actually landed. */
    onUploaded?: (result: { photoIds: number[]; primaryAttachmentId: number | null }) => void;
  }) => {
    const { assetId, files, primaryPhotoIndex = null, fallbackPrimaryAttachmentId = null, label, onUploaded } = input;
    const collectionType = input.collectionType ?? defaultAttachmentCollectionType;
    // Last line of defence for #982. Callers are expected to have confirmed the
    // parent first, but an unresolved id here would POST to
    // /api/v1/assets/undefined/attachments and then blame the failure on a
    // filename. Refuse the run and say what actually happened instead — the
    // files stay with whichever screen queued them.
    if (!isResolvedEntityId(assetId)) {
      if (files.length) {
        // `label` is the household's own text (the thing's name) and passes
        // through untranslated; only the sentence around it has a key. The
        // one/many key is chosen here because `copy` sends its values through
        // `replace`, so a `count` value cannot drive i18next's plural rules.
        showToast(copy(
          files.length === 1 ? "uploads.notSavedYetOne" : "uploads.notSavedYetMany",
          `Couldn't upload ${files.length} ${pluralize(files.length, "file")} for ${label}: it hasn't finished saving yet. Try again once it's saved.`,
          { count: files.length, label }
        ));
      }
      return;
    }
    let photoIds: number[] = [];
    let primaryAttachmentId: number | null = null;
    backgroundUploads.start<Attachment>({
      key: `asset:${assetId}`,
      files,
      upload: (file) => api<Attachment>(`/api/v1/assets/${assetId}/attachments`, {
        method: "POST",
        body: attachmentUploadBody(file, collectionType)
      }),
      finalize: async ({ results }) => {
        photoIds = uploadedPhotoAttachmentIds(results.filter((entry): entry is Attachment => entry != null));
        if (!photoIds.length) return;
        primaryAttachmentId = primaryAttachmentIdForQueuedUpload(results, primaryPhotoIndex) ?? fallbackPrimaryAttachmentId;
        // Read the asset's photos HERE rather than trusting a list captured at
        // submit time. A detached run can outlive the state that started it: a
        // photo deleted meanwhile would make /photo-settings 400 on the stale
        // id, and a second upload on the same asset would omit the first
        // batch's ids and clobber its order. This read is inside finalize, so
        // it sees both batches once they have landed.
        const current = await api<Attachment[]>(`/api/v1/assets/${assetId}/attachments`);
        const currentPhotoIds = uploadedPhotoAttachmentIds(current);
        const ordered = [
          ...currentPhotoIds.filter((id) => !photoIds.includes(id)),
          ...photoIds.filter((id) => currentPhotoIds.includes(id))
        ];
        if (!ordered.length) return;
        const cover = primaryAttachmentId != null && ordered.includes(primaryAttachmentId)
          ? primaryAttachmentId
          : ordered[0] ?? null;
        primaryAttachmentId = cover;
        await reorderAssetPhotos(assetId, ordered, cover);
      },
      onSettled: ({ failedNames, failureReason, cancelled, uploadedCount, finalizeError }) => {
        if (cancelled) return;
        // One toast slot, so both conditions go in one message — a second
        // showToast would erase the first and the user would never learn which
        // files failed (#733 review).
        const problems = [
          failedNames.length ? `${backgroundUploadFailureMessage(label, failedNames, failureReason)}` : "",
          // The photos landed but their order and cover did not, so say so
          // rather than let the save read as fully successful.
          finalizeError ? copy("uploads.orderNotSaved", `${label}: photos uploaded, but their order couldn't be saved`, { label }) : ""
        ].filter(Boolean);
        if (problems.length) showToast(`${problems.join(". ")}. ${copy("uploads.openToRetry", "Open it to try again.")}`);
        if (uploadedCount > 0) {
          onUploaded?.({ photoIds, primaryAttachmentId });
          // Same reasoning as the chore path: the upload's audit row is self=true
          // in the inventory scope and the create that started it just marked
          // that scope, so leaving this to the SSE echo would drop it (#520).
          void refreshAfterOwnChange("inventory");
          // assetAttachments is only ever populated by openAssetDetails, and a
          // tab refresh does not touch it — without this, someone who saves and
          // immediately opens the new thing sees no photos until they navigate
          // away and back. Read through a ref: a state updater must stay pure,
          // and StrictMode would run I/O in it twice.
          const open = selectedAssetRef.current;
          if (open && String(open.id) === String(assetId)) void openAssetDetails(open, { navigate: false });
        }
      }
    });
  }, [openAssetDetails, refreshAfterOwnChange, showToast]);

  useEffect(() => {
    if (!user || tab !== "inventory" || !assetRouteId) return;
    if (selectedAsset?.id === assetRouteId) return;

    const routeAsset = assets.find((asset) => asset.id === assetRouteId);
    if (routeAsset) {
      setAssetNotFound(false);
      setAssetLoadError(null);
      assetNotFoundIdRef.current = null;
      openAssetDetails(routeAsset, { navigate: false }).catch((err) => {
        setError(err instanceof Error ? err.message : "Could not load asset details");
      });
      return;
    }

    // The route id isn't in the in-memory list. It may be a valid-but-unloaded id or a
    // deleted/unknown one (e.g. Back after delete, a stale bookmark). We've already
    // confirmed this exact id is gone, so stay on the not-found state without refetching.
    if (assetNotFoundIdRef.current === assetRouteId) {
      setAssetNotFound(true);
      return;
    }

    // Fetch by id so valid ids that simply aren't loaded yet still resolve, and a 404
    // surfaces a proper "not found" state instead of an infinite "Loading thing" spinner.
    let cancelled = false;
    setAssetNotFound(false);
    setAssetLoadError(null);
    api<Asset>(`/api/v1/assets/${assetRouteId}`)
      .then((asset) => {
        if (cancelled) return;
        assetNotFoundIdRef.current = null;
        void openAssetDetails(asset, { navigate: false });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          assetNotFoundIdRef.current = assetRouteId;
          setAssetNotFound(true);
          return;
        }
        // Non-404 failure (network error, 500, offline): show a retryable error state
        // in the detail panel instead of leaving the spinner forever (#385).
        setAssetLoadError(err instanceof Error ? err.message : "Could not load asset details");
      });
    return () => {
      cancelled = true;
    };
  }, [assetLoadAttempt, assetRouteId, assets, openAssetDetails, selectedAsset?.id, tab, user]);


  const recipeName = useCallback((recipeId?: EntityReferenceId | null) => (
    recipes.find((recipe) => recipe.id === recipeId)?.name ?? "Unassigned meal"
  ), [recipes]);
  const locationName = useCallback((locationId?: EntityReferenceId | null) => (
    locations.find((location) => location.id === locationId)?.path ?? locations.find((location) => location.id === locationId)?.name ?? "No location"
  ), [locations]);
  const accountName = useCallback((accountId?: EntityReferenceId | null) => (
    accounts.find((account) => account.id === accountId)?.name ?? "No account"
  ), [accounts]);

  const switchHousehold = useCallback(async (householdId: string) => {
    if (user?.householdId === householdId) return;
    await run(async () => {
      const status = await postJson<AuthStatus>("/api/v1/account/active-household", { householdId });
      // Awaited, unlike the other call sites: applyAuthStatus below starts
      // loading the new household, and a read that raced the purge would be
      // answered from — or re-populate the cache with — the old one (#1003).
      await clearHouseholdData();
      setScanToken("");
      setAssetRouteId(null);
      setPlaceRouteId(null);
      setProductRouteId(null);
      setRecipeRouteId(null);
      clearAssetDetails();
      // productIdFromPath joins the list (codex review): clearing the state
      // without clearing the URL left /groceries/foods/:id in the address bar
      // pointing at another household's food, and a reload would try to open it.
      if (tab === "scan" || assetIdFromPath(window.location.pathname) || placeIdFromPath(window.location.pathname) || productIdFromPath(window.location.pathname) || recipeIdFromPath(window.location.pathname)) navigateBrowser(tabPath(tab), true);
      // A Finance section is the old household's (#1681): drop the hash and
      // let the view, remounted on the household key below, open Overview.
      if (tab === "finance" && window.location.hash) navigateBrowser(tabPath("finance"), true);
      applyAuthStatus(status);
    }, { refresh: false });
  }, [applyAuthStatus, clearAssetDetails, clearHouseholdData, run, tab, user?.householdId]);

  /** Public-page navigation: a marketing link pushes history and moves publicPath. */
  const navigatePublic = useCallback((path: string) => {
    navigateBrowser(path);
    // Not just setPublicPath: a public-page link can point back into the app —
    // the brand mark and the breadcrumb both go to "/", which for a visitor
    // with a session is the Dashboard. Adopting the whole route keeps the tab
    // and the URL agreeing instead of leaving the stored tab on screen.
    adoptRouteFromLocation();
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [adoptRouteFromLocation]);

  // /signin is a door, not a page to come back to: once the visitor is through
  // it, replace it with the Dashboard so Back does not return to a sign-in form
  // that would immediately redirect again.
  useEffect(() => {
    if (!user) return;
    if ((window.location.pathname.replace(/\/+$/, "") || "/") !== signInPath) return;
    const home = tabPath("dashboard");
    navigateBrowser(home, true);
    setPublicPath(home);
    setActiveTab("dashboard");
    setRouteNotFound(false);
  }, [user]);

  // Returns the purge rather than firing it off, so a caller can wait for the
  // old household's cached responses to be gone before the next session starts
  // (codex review) — the signed-out UI is on screen the moment `run()` resolves.
  const handleSignedOut = useCallback((): Promise<void> => {
    // Back to the public landing page rather than a bare sign-in form.
    navigateBrowser(tabPath("dashboard"), true);
    setPublicPath(tabPath("dashboard"));
    // The URL just moved to a real page, so the not-found state has to go with
    // it (#1259). navigateBrowser replaceStates rather than firing popstate, so
    // adoptRouteFromLocation never runs here — without this, signing out from
    // the 404 and back in rendered "This page does not exist" over "/".
    setRouteNotFound(false);
    const purged = clearHouseholdData();
    setIdentity(null);
    setHouseholds([]);
    setUser(null);
    setExpectedIdentity(null);
    pendingRealtimeUpdatesRef.current = 0;
    pendingSilentRefreshRef.current = false;
    pendingAuthRefreshRef.current = false;
    setPendingRealtimeUpdates(0);
    return purged;
  }, [clearHouseholdData]);

  // A 401 from any household request means the session behind this tab is
  // gone — expired, or ended from another tab (September audit). Until this
  // existed the shell stayed up with the old name in the header over an empty
  // page. Clear the identity so the sign-in form renders where the person is
  // (the URL stays, and signing in returns to it) and say why the form is
  // there. `handleSignedOut` is not reused: it navigates home, and a sign-in
  // from here should land back on the page that was open.
  const signedInRef = useRef(false);
  signedInRef.current = Boolean(user);
  useEffect(() => subscribeSessionExpired(() => {
    if (!signedInRef.current) return;
    void clearHouseholdData();
    setIdentity(null);
    setHouseholds([]);
    setUser(null);
    setExpectedIdentity(null);
    pendingRealtimeUpdatesRef.current = 0;
    pendingSilentRefreshRef.current = false;
    pendingAuthRefreshRef.current = false;
    setPendingRealtimeUpdates(0);
    setError(copy("auth.sessionEnded", "Your session ended. Sign in to continue."));
  }), [clearHouseholdData, setError]);

  async function submitSetup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    await run(async () => {
      const result = await postJson<AuthStatus>("/api/v1/auth/bootstrap", {
        householdName: formValue(form, "householdName"),
        name: formValue(form, "name"),
        email: formValue(form, "email"),
        password: formValue(form, "password")
      });
      applyAuthStatus(result);
      form.reset();
    }, { refresh: false });
  }

  async function submitLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    await run(async () => {
      const result = await postJson<AuthStatus>("/api/v1/auth/login", {
        email: formValue(form, "username"),
        password: formValue(form, "password")
      });
      applyAuthStatus(result);
      form.reset();
    }, { refresh: false });
  }

  async function signInWithGoogle() {
    await run(async () => {
      await openGoogleAuthPopup();
      applyAuthStatus(await api<AuthStatus>("/api/v1/auth/status"));
    }, { refresh: false });
  }

  async function submitHouseholdSignup(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    await run(async () => {
      const result = await postJson<AuthStatus>("/api/v1/households/signup", {
        householdName: formValue(form, "householdName"),
        code: formValue(form, "signupCode")
      });
      applyAuthStatus(result);
      form.reset();
    }, { refresh: false });
  }

  async function submitInvitationAccept(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const token = formValue(form, "invitationToken");
    await run(async () => {
      const result = await postJson<AuthStatus>(`/api/v1/invitations/${encodeURIComponent(token)}/accept`, {});
      applyAuthStatus(result);
      form.reset();
    }, { refresh: false });
  }

  if (!authReady || (!i18nReady && !i18nStartup.hasBeenReady)) {
    return <StartupScreen locale={resolvedLocale} failed={authStartupFailed || i18nStartup.status === "error"}
      onRetry={() => {
        if (authStartupFailed || !authReady) setAuthProbe((count) => count + 1);
        // Browsers may retain a failed dynamic import; reload also obtains a
        // fresh asset manifest after an interrupted deployment.
        else window.location.reload();
      }} />;
  }

  // The server could not be reached at all (September audit). That is not
  // "signed out": a network failure and a 401 used to land on the same sign-in
  // card, so a reload with the connection down asked a signed-in person for
  // their password. Say what happened, and retry when the connection returns.
  if (!user && authUnreachable) {
    return <UnreachableScreen onRetry={() => setAuthProbe((count) => count + 1)} />;
  }

  // The public pages (#833): the landing page for a signed-out visitor on "/",
  // the /product module pages for anyone, and the not-found page for a signed-out
  // visitor on a URL neither this nor the app can name. /signin and any real deep
  // link into the app still fall through to the auth panels below, so a signed-out
  // deep link lands where it was going after sign-in.
  if (publicRoute) {
    return (
      <I18nTransitionBoundary ready={i18nReady} failed={i18nStartup.status === "error"} locale={resolvedLocale}>
      <ViewSuspense>
        <MarketingSiteLazy route={publicRoute} signedIn={hasHousehold} onNavigate={navigatePublic} />
      </ViewSuspense>
      </I18nTransitionBoundary>
    );
  }

  if (!user) {
    return (
      <I18nTransitionBoundary ready={i18nReady} failed={i18nStartup.status === "error"} locale={resolvedLocale}>
      <AuthScreens
        identity={identity}
        authMode={authMode}
        setupRequired={setupRequired}
        googleAuthConfigured={googleAuthConfigured}
        busy={busy}
        error={error}
        onHouseholdSignup={submitHouseholdSignup}
        onInvitationAccept={submitInvitationAccept}
        onSignOut={() => run(() => signOutWithTeardown(
          () => postJson("/api/v1/auth/logout", {}),
          handleSignedOut
        ), { refresh: false })}
        onSetup={submitSetup}
        onLogin={submitLogin}
        onGoogleSignIn={() => void signInWithGoogle()}
        onNavigatePublic={navigatePublic}
      />
      </I18nTransitionBoundary>
    );
  }

  const contextIdentity = identity ?? {
    id: user.id,
    name: user.name,
    email: user.email,
    platformRole: user.platformRole ?? "user"
  } satisfies AuthIdentity;

  return (
    <I18nTransitionBoundary ready={i18nReady} failed={i18nStartup.status === "error"} locale={resolvedLocale}>
    <HouseholdProvider value={{
      tab,
      setTab: navigateToTab,
      demoMode,
      identity: contextIdentity,
      user,
      households,
      switchHousehold,
      applyAuthStatus,
      onSignedOut: handleSignedOut,
      error,
      setError,
      toast,
      showToast,
      dismissToast,
      busy,
      summary,
      dashboardLoadFailures,
      assetValue,
      insuranceCoverage,
      locations,
      assets,
      assetQuery,
      assetRouteId,
      placeRouteId,
      productRouteId,
      openFoodDetails,
      closeFoodDetails,
      assetNotFound,
      assetLoadError,
      retryAssetLoad,
      recipeRouteId,
      setAssetQuery,
      products,
      stockLots,
      expirations,
      recipes,
      mealPlanEntries,
      shoppingLists,
      shoppingTrips,
      shoppingPayloadVersion,
      shoppingPayloadFailures,
      shoppingRefreshing,
      stores,
      accounts,
      categories,
      transactions,
      budgets,
      recurringItems,
      financeOverview,
      audit,
      taskOccurrences,
      choreView,
      setChoreView,
      choreOccurrences,
      choreCategories,
      choreTemplates,
      choreAnalytics,
      choreUsers,
      choreTimeline,
      selectedChore,
      setSelectedChore,
      selectedAsset,
      setSelectedAsset,
      assetMovements,
      assetAttachments,
      scanToken,
      scanData,
      scanLoadError,
      setScanData,
      mealData,
      activeList,
      groceryLists,
      run,
      refreshActiveTab,
      refreshAfterOwnChange,
      beginLocalWrite,
      suppressRealtimeBanner,
      loadDashboard,
      loadInventory,
      loadAssets,
      loadGroceries,
      loadChores,
      loadRecipes,
      loadMealAssembler,
      loadShopping,
      patchShoppingTrip,
      loadFinance,
      reloadScanData,
      completeTaskOccurrence,
      completeTaskOccurrences,
      snoozeTaskOccurrence,
      snoozeTaskOccurrences,
      catchUpTaskOccurrences,
      skipTaskOccurrences,
      createChore,
      uploadChoreAttachments,
      uploadAssetAttachments,
      updateChore,
      deleteChore,
      completeChoreOccurrence,
      snoozeChoreOccurrence,
      skipChoreOccurrence,
      reopenChoreOccurrence,
      applyChoreTemplate,
      openChoreTimeline,
      handleScanToken,
      closeAssetDetails,
      openAssetDetails,
      openPlaceDetails,
      closePlaceDetails,
      openRecipeDetails,
      closeRecipeDetails,
      recipeName,
      locationName,
      accountName
    }}>
      <ListSheetDialogProvider>
        <Shell
          onSignedOut={handleSignedOut}
          onOpenSearch={openCommandPalette}
          demoMode={demoMode}
          routeNotFound={showRouteNotFound}
          realtimeState={realtimeState}
          pendingUpdateCount={pendingRealtimeUpdates}
          online={online}
          pendingSyncCount={pendingSyncCount}
          pendingUploadCount={pendingUploadCount}
          onRetrySync={retryOfflineQueue}
          onApplyUpdates={() => applyRealtimeUpdates().catch((err) => {
            setError(err instanceof Error ? err.message : "Could not refresh household updates");
          })}
        >
          <ActiveFeature
            tab={tab}
            notFound={showRouteNotFound}
            onGoHome={() => navigateToTab("dashboard")}
            releaseNotesVersion={releaseNotesVersion}
            assistantRefreshNonce={assistantRefreshNonce}
            assistantChatRouteId={assistantChatRouteId}
            onAssistantChatRoute={handleAssistantChatRoute}
            householdId={activeHouseholdId}
          />
        </Shell>
        <CommandPalette
          onSelectTab={navigateToTab}
          // No "current page" hint on a URL that matched nothing (September audit).
          activeTab={showRouteNotFound ? undefined : tab}
          open={paletteOpen}
          onOpenChange={setPaletteOpen}
          demoMode={demoMode}
        />
      </ListSheetDialogProvider>
    </HouseholdProvider>
    </I18nTransitionBoundary>
  );
}

/**
 * Every signed-out screen: create-or-join-a-household, the two Cloudflare
 * Access panels, and sign-in / first-run owner setup. This is the FIRST screen
 * a visitor sees and, until #1088, the only one still hardcoded to English —
 * a Spanish household could not sign in in Spanish.
 *
 * A component of its own rather than three branches inside App, because the
 * translator has to come from a hook: App itself renders before initI18n has
 * resolved (App is what starts it), so a `useTranslation` up there would run
 * against a runtime that does not exist yet and warn on every cold load. This
 * only ever mounts BELOW App's `i18nReady` gate, and `common` is one of the two
 * eagerly initialised namespaces, so `t` is live here and needs no ready gate.
 *
 * The panels branch in the same order the three early returns did, so which
 * screen a given auth state lands on is unchanged.
 */
function AuthScreens({
  identity,
  authMode,
  setupRequired,
  googleAuthConfigured,
  busy,
  error,
  onHouseholdSignup,
  onInvitationAccept,
  onSignOut,
  onSetup,
  onLogin,
  onGoogleSignIn,
  onNavigatePublic
}: {
  identity: AuthIdentity | null;
  authMode: AuthStatus["authMode"];
  setupRequired: boolean;
  googleAuthConfigured: boolean;
  busy: boolean;
  error: string;
  onHouseholdSignup: (event: FormEvent<HTMLFormElement>) => void;
  onInvitationAccept: (event: FormEvent<HTMLFormElement>) => void;
  onSignOut: () => void;
  onSetup: (event: FormEvent<HTMLFormElement>) => void;
  onLogin: (event: FormEvent<HTMLFormElement>) => void;
  onGoogleSignIn: () => void;
  onNavigatePublic: (path: string) => void;
}) {
  const { t } = useTranslation("common");

  if (identity) {
    return (
      <main className="auth-page">
        <section className="auth-panel">
          <div>
            <p className="eyebrow">{identity.email}</p>
            <h1>{t("auth.joinTitle")}</h1>
          </div>
          {error ? <p className="alert">{error}</p> : null}
          <p className="inline-note">{t("auth.noHouseholdNote")}</p>
          <form className="form-grid" onSubmit={onHouseholdSignup}>
            <label htmlFor="signup-household">{t("auth.householdNameLabel")}<input id="signup-household" name="householdName" autoComplete="organization" required /></label>
            <label htmlFor="signup-code">{t("auth.signupCodeLabel")}<input id="signup-code" name="signupCode" autoComplete="one-time-code" required /></label>
            <button className="primary" disabled={busy}><Plus size={18} /> {t("auth.createHousehold")}</button>
          </form>
          <form className="form-grid" onSubmit={onInvitationAccept}>
            <label htmlFor="invitation-token">{t("auth.invitationTokenLabel")}<input id="invitation-token" name="invitationToken" autoComplete="one-time-code" required /></label>
            <button disabled={busy}><Check size={18} /> {t("auth.acceptInvitation")}</button>
          </form>
          <button type="button" onClick={onSignOut}>
            {t("auth.signOut")}
          </button>
        </section>
      </main>
    );
  }

  if (authMode === "cloudflare_access") {
    return (
      <main className="auth-page">
        <section className="auth-panel">
          <div>
            {/* The product name, not copy — never translated (#1088). */}
            {/* eslint-disable-next-line i18next/no-literal-string -- the wordmark, not the noun */}
            <p className="eyebrow">Household</p>
            <h1>{setupRequired ? t("auth.localSetupRequired") : t("auth.accessVerified")}</h1>
          </div>
          {error ? <p className="alert">{error}</p> : null}
          <p className="inline-note">
            {setupRequired ? t("auth.cloudflareSetupNote") : t("auth.cloudflareReadyNote")}
          </p>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-page">
      <section className="auth-panel">
        <div>
          {/* The product name, not copy — never translated (#1088). */}
          {/* eslint-disable-next-line i18next/no-literal-string -- the wordmark, not the noun */}
          <p className="eyebrow">Household</p>
          <h1>{setupRequired ? t("auth.createOwnerAccount") : t("auth.signIn")}</h1>
        </div>
        {error ? <p className="alert">{error}</p> : null}
        {setupRequired ? (
          <form className="form-grid" onSubmit={onSetup} method="post" action="/api/v1/auth/bootstrap" autoComplete="on">
            {/* "My Household" is NOT copy: it is a pre-filled value that this
                form SAVES as the household's name. Translating a default that
                becomes stored data would write Spanish into a record every
                other language then reads — the household renames it whenever
                it likes, and the label above it is what gets translated. */}
            <label htmlFor="setup-household">{t("auth.householdNameLabel")}<input id="setup-household" name="householdName" autoComplete="organization" defaultValue="My Household" /></label>
            <label htmlFor="setup-name">{t("auth.nameLabel")}<input id="setup-name" name="name" autoComplete="name" required /></label>
            <label htmlFor="setup-email">{t("auth.emailLabel")}<input id="setup-email" name="email" type="email" inputMode="email" autoComplete="username" required /></label>
            <label htmlFor="setup-password">{t("auth.passwordLabel")}<input id="setup-password" name="password" type="password" minLength={10} autoComplete="new-password" required /></label>
            <button type="submit" className="primary" disabled={busy}><Plus size={18} /> {t("auth.create")}</button>
          </form>
        ) : (
          <>
            {/* An unconfigured Google client is an operator's problem, not the
                visitor's. This used to render the button greyed out under the
                sentence "Google sign-in needs a Google OAuth client configured on
                the API server" — a server note on a public form, above an "or"
                divider that then divided nothing. Settings keeps the operator's
                version of this message (settings.google.setupNote). */}
            {googleAuthConfigured ? (
              <>
                <button
                  type="button"
                  className="google-auth-button"
                  disabled={busy}
                  onClick={onGoogleSignIn}
                >
                  <span className="google-auth-mark" aria-hidden="true">G</span>
                  {t("auth.continueWithGoogle")}
                </button>
                <div className="auth-divider"><span>{t("auth.or")}</span></div>
              </>
            ) : null}
            <form className="form-grid" onSubmit={onLogin} method="post" action="/api/v1/auth/login" autoComplete="on">
              <label htmlFor="login-username">{t("auth.emailLabel")}<input id="login-username" name="username" type="email" inputMode="email" autoComplete="username" required /></label>
              <label htmlFor="login-password">{t("auth.passwordLabel")}<input id="login-password" name="password" type="password" autoComplete="current-password" required /></label>
              <button type="submit" className="primary" disabled={busy}><Check size={18} /> {t("auth.signIn")}</button>
            </form>
          </>
        )}
        {/* A visitor who arrives here from the public pages needs a way back
            to them; without this the sign-in form is a dead end (#833). */}
        <p className="inline-note">
          <a
            href={tabPath("dashboard")}
            onClick={(event) => {
              if (event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
              event.preventDefault();
              onNavigatePublic(tabPath("dashboard"));
            }}
          >
            {t("auth.readAbout")}
          </a>
        </p>
      </section>
    </main>
  );
}

function ActiveFeature({ tab, notFound, onGoHome, releaseNotesVersion, assistantRefreshNonce, assistantChatRouteId, onAssistantChatRoute, householdId }: {
  tab: Tab;
  notFound: boolean;
  householdId: string;
  onGoHome: () => void;
  releaseNotesVersion: string | null;
  assistantRefreshNonce: number;
  assistantChatRouteId: string | null;
  onAssistantChatRoute: (chatId: string | null, options?: { replace?: boolean }) => void;
}) {
  if (notFound) return <RouteNotFoundView onGoHome={onGoHome} />;
  // Each tab is its own chunk (#1678): `views` maps tab -> lazy component, and
  // the Suspense boundary paints the panel skeleton until the chunk lands so a
  // tab switch never flashes empty. The route was parsed before this renders.
  const view = (() => {
    if (tab === "assistant") {
      const AssistantView = views.assistant;
      return <AssistantView refreshNonce={assistantRefreshNonce} routeChatId={assistantChatRouteId} onRouteChatId={onAssistantChatRoute} />;
    }
    if (tab === "release-notes") {
      const ReleaseNotesView = views["release-notes"];
      return <ReleaseNotesView version={releaseNotesVersion} />;
    }
    if (tab === "finance") {
      // Keyed on the household so a switch remounts the view and its hash-borne
      // section starts over at Overview (#1681).
      const FinanceView = views.finance;
      return <FinanceView key={householdId} />;
    }
    const View = views[tab];
    return <View />;
  })();
  // Keyed on the tab so a chunk that failed to load for one view does not leave
  // every other tab behind the reload prompt (review of #1678).
  return <ViewSuspense key={tab} tab={tab}>{view}</ViewSuspense>;
}
